// src/ui/components/EventModal.js
// Controlador único del modal de Eventos.
// - Abre modal create/edit
// - Carga contactos y venues
// - Previews
// - Validación mínima según tu DB
// - Guardado: events + event_contacts(primary) + event_venues(primary opcional)
// - Integra ContactsModal (crear/editar contacto desde aquí)
//
// IMPORTANTE:
// - Este archivo "parchea" los selects Stage/Channel/Type para que coincidan con tu DB,
//   sin obligarte a reescribir EventModal.html ahora mismo.
// - Tu constraint exige rejected_reason si stage = rechazado.
//   Hemos añadido el campo al HTML y lógica para mostrarlo/validarlo.

import { getSupabaseClient } from "../../services/supabaseClient.js";
import { getCurrentTeamId, getRole, getUser } from "../../services/teamService.js";
import { getUserRole } from "../../services/rbac.js";

import { listContactsByTeam } from "../../services/contactsService.js";
import { listVenuesByTeam, ensureVenueDistance } from "../../services/venuesService.js";
import { getPriceForContext } from "../../services/pricingService.js";
import { openCreateContactModal, openEditContactModal } from "./ContactsModal.js";
import { openCreateVenueModal, openEditVenueModal } from "./VenueModal.js";
import { actions } from "../../app/state.js";
import { VALID_REGIONS, normalizeRegion, formatRegionLabel } from "../../constants/regions.js";
import { getExpensesForecastByTier } from "../../services/expensesService.js";
import { getTierInfoForRegion } from "../../constants/regionTiers.js";
import { rankEventConflicts } from "../../utils/eventPriority.js";
import { listTeamMembers } from "../../services/teamService.js";
import { listActivitiesByEvent, getNextActivity, completeActivity, getActivityOutcomeLabel } from "../../services/activitiesService.js";
import { openCreateActivityModal, openEditActivityModal } from "./ActivityModal.js";
import { generatePresupuestoPDF, generateContratoPDF, generateConfirmacionPDF, generateFacturaPDF, generateReciboPDF, generateProformaPDF } from "../../services/pdf/index.js";
import { openDocumentPreflightModal } from "./DocumentPreflightModal.js";
import { getPrimaryContactForEvent } from "../../services/contactsService.js";
import { listEventContacts, addEventContact, removeEventContact, listEventsByDate } from "../../services/eventsService.js";
import { listLineupMembers, getEventLineup, saveEventLineup, getDefaultLineup, getEventLogistics, saveEventLogistics, ROLE_ORDER, SOUND_FREE_REGIONS, PACO_REGIONS, DEFAULT_SOUND_PRICE, DEFAULT_PACO_PRICE } from "../../services/lineupService.js";
import { listUnavailabilityForTeam } from "../../services/memberAvailabilityService.js";
import { fetchContact, fetchEventById, fetchPrimaryContactId, fetchPrimaryVenueId, fetchVenue, upsertPrimaryEventContact, upsertPrimaryEventVenue } from "./EventModal.data.js";
import { setContactPreview, setVenuePreview, setVenueDistancePreview, setEquipmentToggle, getEquipmentToggle } from "./EventModal.previews.js";
import { wireSelectors } from "./EventModal.selectors.js";
import { wireEmailsLogic } from "./EventModal.emails.js";
import { wireInviteLogic } from "./EventModal.invite.js";
import { renderTechnicalSuppliersSummary, wireSuppliersCard } from "./EventModal.suppliers.js";
import { listEventSuppliers } from "../../services/eventSuppliersService.js";
import { technicalSupplierStats } from "../../domain/suppliers.js";
import { applyViewerExpenseMode, wireFinanceLogic } from "./EventModal.finance.js";
import { $, CHANNELS, STAGES, TYPES, escapeHtml, normStr, todayISO } from "./EventModal.utils.js";
import { getLogisticsTasks, createDefaultLogisticsTasks, ensureCoreLogisticsTasks, updateLogisticsTask } from "../../services/logisticsService.js";
import { SUPPLIER_TYPE_LABELS, SUPPLIER_TYPES } from "../../domain/constants.js";
import { wireDocumentsTab } from "./EventModal.documents.js";
import { wireSetlistCard } from "./EventModal.setlist.js";
import { listEventDocuments, getConfirmationStatus, registerIssuedDocument, DOC_KINDS } from "../../services/eventDocumentsService.js";
import { listPaymentsByEvent } from "../../services/paymentsService.js";
import { splitHonorarios } from "../../services/pdf/generateContrato.js";
function stageLabel(v) {
  switch (v) {
    case "lead": return "Solicitud";
    case "info_needed": return "Info pendiente";
    case "contacted": return "Contactado";
    case "meeting": return "Reunión";
    case "proposal_sent": return "Presupuesto";
    case "negotiation": return "Negociación";
    case "accepted_pending_deposit": return "Señal pendiente";
    case "confirmed": return "Confirmado";
    case "done": return "Realizado";
    case "rechazado": return "Perdido";
    default: return v || "Solicitud";
  }
}

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function channelLabel(v) {
  switch (v) {
    case "mail": return "Mail";
    case "mail / wpp": return "Mail / Wpp";
    case "wpp": return "Wpp";
    case "movil": return "Móvil";
    default: return v || "—";
  }
}

function typeLabel(v) {
  switch (v) {
    case "boda": return "Boda";
    case "empresa": return "Empresa";
    case "privado": return "Privado";
    default: return v || "—";
  }
}

function setSelectOptions(selectEl, items, { placeholder = "—", getValue, getLabel } = {}) {
  if (!selectEl) return;
  const current = selectEl.value;
  selectEl.innerHTML = "";

  const opt0 = document.createElement("option");
  opt0.value = "";
  opt0.textContent = placeholder;
  selectEl.appendChild(opt0);

  for (const it of items) {
    const o = document.createElement("option");
    o.value = getValue(it);
    o.textContent = getLabel(it);
    selectEl.appendChild(o);
  }

  if (current) selectEl.value = current;
}

// function showErrors moved to bottom


async function requireSession() {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data?.user) throw new Error("No hay sesión activa.");
  return data.user;
}

function setEditMode(root, isEditable) {
  // Persist state for dynamic renders
  root.setAttribute("data-is-editable", isEditable ? "true" : "false");

  // General status fields
  const mode = $(root, "#event-modal-mode")?.textContent;

  // Disable fields
  root.querySelectorAll("input, select, textarea").forEach((el) => {
    if (el.closest("#event-secondary-contact-picker")) {
      el.disabled = false;
      el.classList.remove("bg-slate-50", "cursor-not-allowed");
      return;
    }

    if (el.closest("#event-tab-content-lineup")) {
      el.disabled = false;
      el.classList.remove("bg-slate-50", "cursor-not-allowed");
      return;
    }

    // Exclude finance related inputs from being disabled (Allow adding expenses without edit mode)
    if (el.id?.startsWith("finance-exp-") || el.id?.startsWith("event-payment-")) {
      el.disabled = false;
      el.classList.remove("bg-slate-50", "cursor-not-allowed");
    } else {
      el.disabled = !isEditable;
      if (!isEditable) el.classList.add("bg-slate-50", "cursor-not-allowed");
      else el.classList.remove("bg-slate-50", "cursor-not-allowed");
    }
  });

  // Switch "Incluye equipo" (no es input/select/textarea, se gestiona aparte)
  const equipmentToggleBtn = $(root, "#event-equipment-included");
  if (equipmentToggleBtn) {
    equipmentToggleBtn.disabled = !isEditable;
    equipmentToggleBtn.classList.toggle("opacity-50", !isEditable);
    equipmentToggleBtn.classList.toggle("cursor-not-allowed", !isEditable);
  }

  // Footer Buttons
  const saveBtn = $(root, "#event-modal-save");
  if (saveBtn) saveBtn.classList.toggle("hidden", !isEditable);

  const deleteBtn = $(root, "#event-modal-delete");
  if (deleteBtn && mode === "edit") {
    deleteBtn.classList.toggle("hidden", !isEditable);
  }

  // Header toggle
  const editToggle = $(root, "#event-modal-edit-toggle");
  if (editToggle) {
    editToggle.classList.toggle("hidden", isEditable || mode === "create");
  }

  // Inner action buttons (Removed create/add buttons to keep them always available)
  const actionsList = [
    "#event-contact-edit", "#event-contact-clear",
    "#event-venue-edit", "#event-venue-clear",
    "#event-next-activity-complete"
  ].join(",");

  root.querySelectorAll(actionsList).forEach(btn => btn.classList.toggle("hidden", !isEditable));

  root.querySelectorAll("#event-contact-add-secondary, #event-secondary-contact-picker button").forEach(btn => {
    btn.disabled = false;
    btn.classList.remove("bg-slate-50", "cursor-not-allowed");
  });

  // Dynamic list buttons (like trash icons)
  root.querySelectorAll(".remove-contact-btn, .delete-payment-btn, .delete-expense-btn").forEach(btn => {
    btn.classList.toggle("hidden", !isEditable);
  });
}

function patchModalSelectsToDB(root) {
  // Stage
  const st = $(root, "#event-stage");
  if (st) {
    const current = st.value;
    st.innerHTML = "";
    for (const s of STAGES) {
      const o = document.createElement("option");
      o.value = s;
      o.textContent = stageLabel(s);
      st.appendChild(o);
    }
    st.value = STAGES.includes(current) ? current : "lead";
  }

  // Channel (incluye vacío)
  const ch = $(root, "#event-channel");
  if (ch) {
    const current = ch.value;
    ch.innerHTML = "";

    const empty = document.createElement("option");
    empty.value = "";
    empty.textContent = "—";
    ch.appendChild(empty);

    for (const c of CHANNELS) {
      const o = document.createElement("option");
      o.value = c;
      o.textContent = channelLabel(c);
      ch.appendChild(o);
    }
    ch.value = (current && CHANNELS.includes(current)) ? current : "";
  }

  // Type (incluye vacío)
  const tp = $(root, "#event-type");
  if (tp) {
    const current = tp.value;
    tp.innerHTML = "";

    const empty = document.createElement("option");
    empty.value = "";
    empty.textContent = "—";
    tp.appendChild(empty);

    for (const t of TYPES) {
      const o = document.createElement("option");
      o.value = t;
      o.textContent = typeLabel(t);
      tp.appendChild(o);
    }
    tp.value = (current && TYPES.includes(current)) ? current : "";
  }
}

function readForm(root) {
  return {
    client_name_legacy: normStr($(root, "#event-name")?.value),
    date: normStr($(root, "#event-date")?.value),
    stage: normStr($(root, "#event-stage")?.value) || "lead",
    channel: normStr($(root, "#event-channel")?.value) || null,
    type: normStr($(root, "#event-type")?.value) || null,
    region: normalizeRegion($(root, "#event-region")?.value), // Validación canonical
    amount: $(root, "#event-price")?.value === "" ? null : Number($(root, "#event-price")?.value),
    equipment_included: getEquipmentToggle(root),
    notes: normStr($(root, "#event-notes")?.value) || null,
    primaryContactId: normStr($(root, "#event-contact-select")?.value),
    primaryVenueId: normStr($(root, "#event-venue-select")?.value),
    encargado_id: normStr($(root, "#event-encargado")?.value) || null,
    rejected_reason: normStr($(root, "#event-rejected-reason")?.value) || null
  };
}

function validateForm(values) {
  const errors = [];

  if (!values.client_name_legacy) errors.push("Nombre (cliente) es obligatorio.");
  if (!values.date) errors.push("Fecha es obligatoria.");

  if (values.stage && !STAGES.includes(values.stage)) {
    errors.push("Stage inválido (no coincide con la base de datos).");
  }

  // En "Solicitud" (lead) se puede guardar sin encargado; para pasar a cualquier
  // otra etapa el evento tiene que tener encargado asignado.
  if (!values.encargado_id && values.stage !== "lead") {
    errors.push("Debes asignar un encargado para mover el evento fuera de Solicitud.");
  }

  if (values.channel && !CHANNELS.includes(values.channel)) {
    errors.push("Canal inválido (no coincide con la base de datos).");
  }

  if (values.type && !TYPES.includes(values.type)) {
    errors.push("Tipo inválido (no coincide con la base de datos).");
  }

  // Validación de region
  const rawRegion = $(document, "#event-region")?.value; // check raw value in DOM if possible, or just trust normalizeRegion result logic
  // normalizeRegion devuelve null si no existe.
  // Si tenemos rawRegion pero values.region es null, significa que era inválida.
  // Pero como usamos un select populado por nosotros, es difícil que sea inválida.
  // Sin embargo, si values.region es null y el campo es obligatorio (no lo es según schema, es text NULL), todo OK.
  // Si quieres forzar que si seleccionan algo sea válido:
  // (Ya lo hace el select).


  if (values.amount != null) {
    const n = Number(values.amount);
    if (!Number.isFinite(n) || n < 0) errors.push("Precio debe ser un número >= 0.");
  }

  if (!values.primaryContactId) errors.push("Debes seleccionar un contacto principal.");

  if (values.stage === "rechazado") {
    if (!values.rejected_reason) {
      errors.push("El campo 'Motivo de pérdida' es obligatorio para eventos en etapa Perdido.");
    }
  }

  return errors;
}

async function saveEvent({ mode, eventId, teamId, root }) {
  const supabase = getSupabaseClient();

  const values = readForm(root);
  const errors = validateForm(values);
  showErrors(root, errors);
  if (errors.length) return { ok: false };

  const user = await requireSession();

  const payload = {
    team_id: teamId,
    client_name_legacy: values.client_name_legacy,
    date: values.date,
    stage: values.stage,
    channel: values.channel,
    type: values.type,
    region: values.region,
    amount: values.amount,
    equipment_included: values.equipment_included,
    notes: values.notes,
    owner_id: values.encargado_id,
    rejected_reason: values.rejected_reason
  };

  // Traer evento actual si es update para comparar stage
  let currentEvent = null;
  if (mode === "update" || mode === "edit") {
    const { data } = await supabase.from("events").select("stage, won_time, done_at, confirmed_at, deposit_received_at").eq("id", eventId).single();
    currentEvent = data;
  }

  // --- Aviso de conflicto de fecha: al ocupar el día (confirmar o dejar señal pendiente) ---
  // Una señal pendiente reserva el día igual que un confirmado. El aviso salta al ENTRAR
  // en un estado que ocupa el día desde uno que no lo ocupaba (no re-avisa señal->confirmado).
  const stageOccupiesDay = (s) => s === "confirmed" || s === "accepted_pending_deposit";
  if (stageOccupiesDay(values.stage) && (!currentEvent || !stageOccupiesDay(currentEvent.stage))) {
    const shouldContinue = await confirmBlockingDateConflict(values.date, { excludeId: eventId, teamId });
    if (!shouldContinue) return { ok: false };
  }

  // --- Aviso (no bloqueo) de documentacion al confirmar ---
  // No todo evento pasa por contrato firmado y justificante: los propios o los
  // de trato directo no lo tienen. Se avisa, pero la decision es del usuario.
  if (values.stage === "confirmed" && currentEvent && currentEvent.stage !== "confirmed" && eventId) {
    const shouldContinue = await confirmMissingDocuments(
      eventId,
      Number(values.amount) || 0,
      currentEvent.deposit_pct
    );
    if (!shouldContinue) return { ok: false };
  }

  // Si el stage cambia, actualizamos campos de auditoría
  if (!currentEvent || currentEvent.stage !== values.stage) {
    payload.stage_updated_at = new Date();

    // Histórico de estados
    // Se delega a DB trigger o en UI si se quiere manual, la DB usa la columna `stage_history`

    // Pasa a confirmed
    if (values.stage === "confirmed") {
      if (!currentEvent || !currentEvent.confirmed_at) {
        payload.confirmed_at = new Date();
        // Si no se habia registrado recibo de deposito, ponemos el mismo dia
        if (!currentEvent || !currentEvent.deposit_received_at) {
          payload.deposit_received_at = new Date();
        }
      }
    }

    if (values.stage === "confirmed" || values.stage === "done") {
      if (!currentEvent || !currentEvent.won_time) {
        payload.won_time = new Date();
      }
    } else {
      payload.won_time = null;
    }

    if (values.stage === "done") {
      if (!currentEvent || !currentEvent.done_at) {
        payload.done_at = new Date();
        payload.done_by = user.id;
      }
    } else {
      payload.done_at = null;
      payload.done_by = null;
    }
  }

  let saved;
  if (mode === "create") {
    const { data, error } = await supabase.from("events").insert(payload).select("*").single();
    if (error) throw error;
    saved = data;
  } else {
    const { data, error } = await supabase
      .from("events")
      .update(payload)
      .eq("id", eventId)
      .select("*")
      .single();
    if (error) throw error;
    saved = data;
  }

  // contact primary (obligatorio)
  await upsertPrimaryEventContact({
    eventId: saved.id,
    contactId: values.primaryContactId,
  });

  // venue primary (opcional)
  if (values.primaryVenueId) {
    await upsertPrimaryEventVenue({
      teamId,
      eventId: saved.id,
      venueId: values.primaryVenueId,
    });
  }

  if (saved.stage === "confirmed") {
    try {
      await createDefaultLogisticsTasks(saved.id, saved.team_id || teamId);
    } catch (taskErr) {
      console.warn("Could not create default logistics tasks:", taskErr);
      actions.addToast?.({ type: "error", message: "Evento confirmado, pero no pude crear el checklist logistico." });
    }
  }

  return { ok: true, event: saved };
}

function wireBaseButtons(root, closeFn) {
  $(root, "#event-modal-close")?.addEventListener("click", closeFn);
  $(root, "#event-modal-cancel")?.addEventListener("click", closeFn);
}

async function loadAndFillContacts(root, teamId, { selectId = null } = {}) {
  const contacts = await listContactsByTeam(teamId, { orderBy: "name", ascending: true, select: "*" });
  root._contactsData = contacts; // Store for combobox

  const contactInput = $(root, "#event-contact-select");
  const contactSearch = $(root, "#event-contact-search");

  if (selectId && contactInput) {
    contactInput.value = selectId;
    const c = await fetchContact(selectId);
    if (contactSearch && c) {
      const full = [c.name, c.surname].filter(Boolean).join(" ").trim();
      const meta = c.company ? ` · ${c.company}` : "";
      contactSearch.value = `${full || "—"}${meta}`;
    }
    setContactPreview(root, c);
  } else {
    if (contactInput) contactInput.value = "";
    if (contactSearch) contactSearch.value = "";
    setContactPreview(root, null);
  }

  return contacts;
}

async function loadAndFillVenues(root, teamId, { selectId = null } = {}) {
  const venues = await listVenuesByTeam(teamId, { orderBy: "name", ascending: true, select: "*" });
  root._venuesData = venues; // Store for combobox

  const venueInput = $(root, "#event-venue-select");
  const venueSearch = $(root, "#event-venue-search");

  if (selectId && venueInput) {
    venueInput.value = selectId;
    const v = await fetchVenue(selectId);
    if (venueSearch && v) venueSearch.value = v.name || "";
    setVenuePreview(root, v);
    updateVenueDistanceAndPrice(root, teamId, v);
  } else {
    if (venueInput) venueInput.value = "";
    if (venueSearch) venueSearch.value = "";
    setVenuePreview(root, null);
    updateVenueDistanceAndPrice(root, teamId, null);
  }

  return venues;
}

/**
 * Busca la regla de pricing_rules que aplica para estos km/region/switch de
 * equipo actual, y si la encuentra sustituye el hint de "Datos generales"
 * por el precio exacto (mas preciso que el tramo por provincia). Si no hay
 * regla que aplique, deja el hint por tramo (renderPriceHint), que ya sabe
 * mostrar "sin precio de referencia" cuando corresponde.
 * @returns {Promise<string>} el label de precio para el bloque de distancia
 */
async function applyVenuePriceHint(root, teamId, { km, region }) {
  renderPriceHint(root, region);

  let priceLabel = "";
  try {
    const rule = await getPriceForContext(teamId, { km, region, equipmentIncluded: getEquipmentToggle(root) });
    if (rule) {
      const priceFmt = Number(rule.price_eur).toLocaleString("es-ES");
      priceLabel = `Precio sugerido: ${priceFmt} EUR + IVA`;

      // Sustituye el hint por tramo de "Datos generales" por el exacto.
      const hintEl = $(root, "#event-price-hint");
      if (hintEl) {
        hintEl.textContent = `Precio sugerido: ${priceFmt} EUR + IVA (${km.toLocaleString("es-ES")} km desde salida)`;
        hintEl.classList.remove("hidden");
      }
    }
  } catch (e) { }

  return priceLabel;
}

/**
 * Recalcula el precio sugerido por km del venue ya vinculado cuando cambia
 * el switch "Incluye equipo", sin volver a geocodificar (usa el ultimo
 * km/region calculados). No hace nada si no hay ningun venue con km
 * calculado todavia.
 */
async function refreshVenuePriceForEquipment(root, teamId) {
  const km = root._lastVenueKm;
  if (!Number.isFinite(km)) return;

  const priceLabel = await applyVenuePriceHint(root, teamId, { km, region: root._lastVenueRegion });
  setVenueDistancePreview(root, { km, priceLabel });
}

/**
 * Calcula (o recupera del cache del venue) los km desde el origen y, si
 * hay una regla de pricing_rules que aplica, el precio sugerido. Ignora
 * llamadas obsoletas si el usuario cambia de venue mientras se calcula.
 */
async function updateVenueDistanceAndPrice(root, teamId, venue) {
  const token = (root._venueDistanceToken = (root._venueDistanceToken || 0) + 1);

  if (!venue || (!venue.address && !venue.city && !venue.region)) {
    root._lastVenueKm = null;
    root._lastVenueRegion = null;
    setVenueDistancePreview(root, null);
    return;
  }

  setVenueDistancePreview(root, { loading: true });

  // Sincroniza el select de región (Datos generales) con la del venue, y
  // deja que su listener normal (updateForecast) refresque el hint por
  // tramo mientras esperamos el precio exacto por km.
  const regionSel = $(root, "#event-region");
  if (regionSel && venue.region && VALID_REGIONS.includes(venue.region) && regionSel.value !== venue.region) {
    regionSel.value = venue.region;
    regionSel.dispatchEvent(new Event("change", { bubbles: true }));
  }

  let updated;
  try {
    updated = await ensureVenueDistance(venue);
  } catch (e) {
    console.warn("[venue-distance] ensureVenueDistance fallo:", e);
    updated = null;
  }

  if (root._venueDistanceToken !== token) return; // stale

  // Ojo: Number(null) === 0, así que hay que descartar null/undefined
  // explícitamente antes de convertir, o un cálculo fallido (km_from_origin
  // sigue en null) se mostraría como "0 km" en vez de ocultarse.
  const rawKm = updated?.km_from_origin;
  if (rawKm === null || rawKm === undefined) {
    console.warn("[venue-distance] No se pudo geocodificar/calcular km para el venue:", venue.name || venue.id);
    setVenueDistancePreview(root, null);
    return;
  }

  const km = Number(rawKm);
  if (!Number.isFinite(km)) {
    setVenueDistancePreview(root, null);
    return;
  }

  root._lastVenueKm = km;
  root._lastVenueRegion = updated.region;

  const priceLabel = await applyVenuePriceHint(root, teamId, { km, region: updated.region });

  if (root._venueDistanceToken !== token) return; // stale
  setVenueDistancePreview(root, { km, priceLabel });
}

async function loadAndFillRegions(root, { selectId = null } = {}) {
  const sel = $(root, "#event-region");
  if (!sel) return;

  const current = sel.value;
  sel.innerHTML = "";

  const def = document.createElement("option");
  def.value = "";
  def.textContent = "—";
  sel.appendChild(def);

  for (const r of VALID_REGIONS) {
    const opt = document.createElement("option");
    opt.value = r;
    opt.textContent = formatRegionLabel(r);
    sel.appendChild(opt);
  }

  if (selectId && VALID_REGIONS.includes(selectId)) {
    sel.value = selectId;
  } else if (current && VALID_REGIONS.includes(current)) {
    sel.value = current;
  }

  // Trigger forecast update if value selected
  if (sel.value) updateForecast(root, sel.value);
}

/**
 * Actualiza el hint de "Precio sugerido" segun el tramo de la region Y el
 * estado actual del switch "Incluye equipo". Solo tenemos un precio
 * guardado por tramo (el de su regla por defecto); si el switch no
 * coincide con ese default, avisamos que no hay precio de referencia en
 * vez de inventar un numero.
 */
function renderPriceHint(root, region) {
  const hintEl = $(root, "#event-price-hint");
  if (!hintEl) return;

  if (!region) {
    hintEl.classList.add("hidden");
    return;
  }

  const tierInfo = getTierInfoForRegion(region);
  if (!tierInfo?.basePrice) {
    hintEl.classList.add("hidden");
    return;
  }

  const equipmentOn = getEquipmentToggle(root);
  if (Boolean(tierInfo.productionIncluded) === equipmentOn) {
    hintEl.textContent = `Precio sugerido: ${tierInfo.basePrice.toLocaleString("es-ES")} EUR + IVA. ${tierInfo.productionLabel}.`;
  } else {
    const withOrWithout = equipmentOn ? "con equipo" : "sin equipo";
    hintEl.textContent = `No tenemos precio de referencia para este tramo ${withOrWithout}. Ajusta el precio manualmente.`;
  }
  hintEl.classList.remove("hidden");
}

async function updateForecast(root, region) {
  renderPriceHint(root, region);

  const finBox = $(root, "#event-finance-benchmark");
  if (!finBox) return;

  if (!region) {
    finBox.innerHTML = '<p class="italic text-blue-400 font-normal">Selecciona una región para ver la estimación histórica.</p>';
    return;
  }

  finBox.innerHTML = '<div class="flex items-center gap-2"><i class="fa-solid fa-spinner fa-spin text-blue-400"></i> <span class="text-blue-500">Calculando...</span></div>';

  try {
    const data = await getExpensesForecastByTier(region);
    const tierInfo = getTierInfoForRegion(region);

    // Tier info badge
    const tierBadge = tierInfo
      ? `<div class="flex items-center gap-2 mb-2 text-xs">
           <span class="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold">Tramo ${tierInfo.tier}</span>
           <span class="text-slate-500">${tierInfo.distance} - ${tierInfo.label} - Precio sugerido: ${tierInfo.basePrice.toLocaleString("es-ES")} EUR + IVA - ${tierInfo.productionLabel}</span>
         </div>`
      : "";

    if (!data || !data.forecast_total) {
      finBox.innerHTML = tierBadge + '<span class="italic text-blue-400 font-normal">No hay histórico de gastos para este tramo.</span>';
      return;
    }

    const forecastTotal = Number(data.forecast_total || 0);
    const breakdown = data.forecast_by_category || {};
    let rows = Object.entries(breakdown).map(([cat, val]) => ({ cat, val: Number(val) }));
    rows.sort((a, b) => b.val - a.val);

    const isTierAvg = data.method === "tier_avg";
    const methodLabel = isTierAvg
      ? `<span class="text-[10px] text-amber-600 font-bold uppercase tracking-wider"><i class="fa-solid fa-triangle-exclamation mr-1"></i>Media del tramo (sin histórico directo)</span>`
      : `<span class="text-[10px] text-emerald-600 font-bold uppercase tracking-wider"><i class="fa-solid fa-check-circle mr-1"></i>Histórico directo de la región</span>`;

    const rowsHtml = rows.map(r =>
      `<div class="flex justify-between"><span>${r.cat}</span><span class="font-bold">${r.val.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€</span></div>`
    ).join("");

    finBox.innerHTML = `
      ${tierBadge}
      ${methodLabel}
      <div class="flex justify-between font-bold border-b border-blue-100 pb-1 mb-1 mt-1 text-blue-900">
         <span>Total medio (${data.sample_size || 0} eventos)</span>
         <span>${forecastTotal.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€</span>
      </div>
      <div class="space-y-1">
        ${rowsHtml}
      </div>
    `;
  } catch (err) {
    console.error(err);
    finBox.innerHTML = '<span class="text-red-400">Error cargando estimación</span>';
  }
}

// listTeamMembers removed here (imported from service)

async function loadAndFillEncargados(root, teamId, { selectId = null } = {}) {
  // Use centralized service
  const members = await listTeamMembers(teamId);
  const sel = $(root, "#event-encargado");
  if (!sel) return members;

  const current = sel.value;

  sel.innerHTML = "";
  const o0 = document.createElement("option");
  o0.value = "";
  o0.textContent = "— Sin asignar —";
  sel.appendChild(o0);

  for (const m of members) {
    const o = document.createElement("option");
    o.value = m.user_id;
    o.textContent = m.display_name || "Sin nombre";
    sel.appendChild(o);
  }

  if (selectId) sel.value = selectId;
  else if (current) sel.value = current;

  return members;
}

function showErrors(root, messages) {
  // Ya no usamos el contenedor de errores en el modal
  const box = $(root, "#event-modal-errors");
  if (box) box.classList.add("hidden");

  if (!messages.length) return;

  // Mostrar cada error como un toast
  messages.forEach(msg => {
    actions.addToast?.({ type: "error", message: msg });
  });
}


function getSelectorDeps() {
  return {
    loadAndFillContacts,
    loadAndFillEncargados,
    loadAndFillVenues,
    loadAndFillRegions,
    updateForecast,
    renderPriceHint,
    refreshVenuePriceForEquipment,
    wireVenueCombobox,
    renderDateConflicts,
  };
}

// =========================
// DATE CONFLICT ALERTS
// =========================
const STAGE_LABELS = {
  lead: "Lead", contacted: "Contactado", meeting: "Reunión",
  proposal_sent: "Presupuesto", negotiation: "Negociación",
  confirmed: "Confirmado", done: "Realizado"
};

/**
 * Render date conflict alerts below the date field.
 * Shows red alert for confirmed/done events and yellow info for pipeline events.
 */
async function renderDateConflictsLegacy(root, date, excludeId = null) {
  const container = $(root, "#event-date-conflicts");
  if (!container) return;

  if (!date) {
    container.classList.add("hidden");
    container.innerHTML = "";
    return;
  }

  try {
    const events = await listEventsByDate(date, { excludeId });
    if (!events.length) {
      container.classList.add("hidden");
      container.innerHTML = "";
      return;
    }

    const closed = events.filter(e => e.stage === "confirmed" || e.stage === "done");
    const pipeline = events.filter(e => e.stage !== "confirmed" && e.stage !== "done");

    let html = "";

    if (closed.length) {
      html += `<div style="background:#fef2f2;border:1px solid #fca5a5;border-radius:12px;padding:10px 14px;margin-bottom:6px;">`;
      html += `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;"><i class="fa-solid fa-triangle-exclamation" style="color:#dc2626;"></i><strong style="color:#991b1b;font-size:13px;">Fecha ocupada</strong></div>`;
      for (const ev of closed) {
        html += `<div style="font-size:12px;color:#7f1d1d;padding:2px 0;">• <strong>${ev.client_name_legacy || "Sin nombre"}</strong> (${ev.type || "—"}) — ${STAGE_LABELS[ev.stage] || ev.stage}</div>`;
      }
      html += `</div>`;
    }

    if (pipeline.length) {
      html += `<div style="background:#fefce8;border:1px solid #fde68a;border-radius:12px;padding:10px 14px;">`;
      html += `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;"><i class="fa-solid fa-circle-info" style="color:#ca8a04;"></i><span style="color:#854d0e;font-size:13px;font-weight:600;">+${pipeline.length} evento${pipeline.length > 1 ? "s" : ""} en pipeline</span></div>`;
      for (const ev of pipeline) {
        html += `<div style="font-size:12px;color:#713f12;padding:2px 0;">• <strong>${ev.client_name_legacy || "Sin nombre"}</strong> (${ev.type || "—"}) — ${STAGE_LABELS[ev.stage] || ev.stage}</div>`;
      }
      html += `</div>`;
    }

    container.innerHTML = html;
    container.classList.remove("hidden");
  } catch (err) {
    console.error("Error fetching date conflicts:", err);
    container.classList.add("hidden");
  }
}

function getCurrentConflictCandidate(root, date, excludeId, teamId) {
  const values = readForm(root);
  return {
    id: excludeId || "__current_event__",
    client_name_legacy: values.client_name_legacy || "Este evento",
    stage: values.stage || "lead",
    type: values.type || null,
    date,
    team_id: teamId || null,
    amount: values.amount,
    region: values.region,
    channel: values.channel,
    primary_venue_name: root._venuesData?.find(v => String(v.id) === String(values.primaryVenueId))?.name || null,
    forecast_expense: 0,
    _isCurrentCandidate: true,
  };
}

function formatPriorityMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "-";
  return `${Math.round(n).toLocaleString("es-ES")} EUR`;
}

function renderRankedConflictRow(ev, index, winnerId) {
  const p = ev._priority || {};
  const isWinner = ev.id === winnerId;
  const isCurrent = ev._isCurrentCandidate;
  const reservedClass = p.isReserved ? "bg-red-50 text-red-700 border-red-100" : "bg-amber-50 text-amber-700 border-amber-100";
  const rankClass = isWinner ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700";
  const rowClass = isCurrent
    ? "border-blue-200 bg-blue-50/70"
    : isWinner
      ? "border-emerald-200 bg-emerald-50/70"
      : "border-slate-200 bg-white";
  const venue = ev.primary_venue_name || ev.primary_venue?.name || "";

  return `
    <div class="rounded-xl border ${rowClass} p-3">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <span class="w-6 h-6 rounded-full ${rankClass} text-[11px] font-black flex items-center justify-center">${index + 1}</span>
            <strong class="text-sm text-slate-900 truncate">${escapeHtml(ev.client_name_legacy || "Evento sin nombre")}</strong>
            ${isCurrent ? `<span class="text-[10px] font-black uppercase tracking-wider text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">Actual</span>` : ""}
          </div>
          <div class="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
            <span>${escapeHtml(typeLabel(ev.type))}</span>
            <span>${escapeHtml(p.regionLabel || "Sin zona")}</span>
            <span>${escapeHtml(venue || "Sin venue")}</span>
            <span>${escapeHtml(STAGE_LABELS[ev.stage] || stageLabel(ev.stage))}</span>
          </div>
        </div>
        <div class="text-right flex-shrink-0">
          <div class="text-xl font-black text-slate-900 leading-none">${p.score || 0}</div>
          <div class="text-[10px] uppercase tracking-wider text-slate-400 font-bold">ranking</div>
        </div>
      </div>

      <div class="mt-3 grid grid-cols-3 gap-2 text-[11px]">
        <div class="rounded-lg bg-white/80 border border-white px-2 py-1">
          <div class="text-slate-400 font-bold uppercase">Precio</div>
          <div class="font-black text-slate-800">${formatPriorityMoney(p.amount)}</div>
        </div>
        <div class="rounded-lg bg-white/80 border border-white px-2 py-1">
          <div class="text-slate-400 font-bold uppercase">Coste est.</div>
          <div class="font-black text-slate-800">${formatPriorityMoney(p.estimatedCost)}</div>
        </div>
        <div class="rounded-lg bg-white/80 border border-white px-2 py-1">
          <div class="text-slate-400 font-bold uppercase">Margen</div>
          <div class="font-black ${p.net >= 0 ? "text-emerald-700" : "text-red-700"}">${formatPriorityMoney(p.net)}</div>
        </div>
      </div>

      <div class="mt-2 flex flex-wrap gap-1.5">
        <span class="text-[10px] font-bold px-2 py-0.5 rounded-full border ${reservedClass}">${p.isReserved ? "Bloquea fecha" : "Pipeline"}</span>
        ${(p.reasons || []).slice(0, 3).map(reason => `<span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">${escapeHtml(reason)}</span>`).join("")}
      </div>
    </div>
  `;
}

function renderRankingInfoText() {
  return `
    <div class="rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
      <div class="flex items-start gap-2">
        <i class="fa-solid fa-circle-info mt-0.5 text-blue-600"></i>
        <div>
          <strong>Como se calcula:</strong>
          prioriza margen neto, cercania, boda, estado mas avanzado, bajo riesgo logistico y misma banda/equipo.
          Penaliza eventos lejos, caros de producir, sin precio o aun frios en pipeline.
        </div>
      </div>
    </div>
  `;
}

function openConflictRankingModal(root) {
  const data = root._dateConflictRanking;
  if (!data?.ranked?.length) return;

  document.querySelector("[data-modal='event-ranking-conflicts']")?.remove();

  const { date, events, ranked, winner, currentRank, closed, pipeline } = data;
  const dateStr = date
    ? new Date(`${date}T00:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })
    : "fecha seleccionada";
  const bestText = winner?._isCurrentCandidate
    ? "El evento actual sale como mejor opcion segun el ranking."
    : `${winner?.client_name_legacy || "Otro evento"} sale como mejor opcion segun el ranking.`;

  const modal = document.createElement("div");
  modal.dataset.modal = "event-ranking-conflicts";
  modal.className = "fixed inset-0 z-[9999] bg-slate-900/50 flex items-center justify-center p-4";
  modal.innerHTML = `
    <div class="bg-white w-full max-w-3xl max-h-[88vh] rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
      <div class="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
        <div>
          <h3 class="text-lg font-black text-slate-900">Ranking de eventos</h3>
          <p class="text-sm text-slate-500">${escapeHtml(dateStr)} - ${events.length} conflicto${events.length > 1 ? "s" : ""} - actual #${currentRank} de ${ranked.length}</p>
        </div>
        <button type="button" class="w-9 h-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 flex items-center justify-center" data-ranking-close title="Cerrar">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="p-5 overflow-y-auto space-y-3">
        ${renderRankingInfoText()}

        <div class="rounded-xl border ${closed.length ? "border-red-200 bg-red-50 text-red-900" : "border-amber-200 bg-amber-50 text-amber-900"} p-3">
          <div class="flex items-start justify-between gap-3">
            <div>
              <div class="flex items-center gap-2">
                <i class="fa-solid ${closed.length ? "fa-triangle-exclamation" : "fa-circle-info"}"></i>
                <strong class="text-sm">${closed.length ? "Fecha con eventos cerrados" : "Fecha con eventos en pipeline"}</strong>
              </div>
              <div class="mt-1 text-xs">
                ${closed.length} cerrado${closed.length !== 1 ? "s" : ""} y ${pipeline.length} en pipeline. ${escapeHtml(bestText)}
              </div>
            </div>
            <div class="text-right text-xs font-bold">
              <div>Mejor opcion</div>
              <div class="text-sm">${escapeHtml(winner?.client_name_legacy || "-")}</div>
            </div>
          </div>
        </div>

        <div class="space-y-2">
          ${ranked.map((ev, idx) => renderRankedConflictRow(ev, idx, winner?.id)).join("")}
        </div>
      </div>
    </div>
  `;

  const close = () => modal.remove();
  modal.addEventListener("click", (ev) => {
    if (ev.target === modal || ev.target.closest("[data-ranking-close]")) close();
  });
  document.body.appendChild(modal);
}

async function renderDateConflicts(root, date, excludeId = null, teamId = null) {
  const container = $(root, "#event-date-conflicts");
  if (!container) return;

  if (!date) {
    root._dateConflictRanking = null;
    container.classList.add("hidden");
    container.innerHTML = "";
    return;
  }

  try {
    const events = await listEventsByDate(date, { excludeId });
    const candidate = getCurrentConflictCandidate(root, date, excludeId, teamId);
    const ranked = rankEventConflicts([candidate, ...events], { currentTeamId: teamId });
    const winner = ranked[0];
    const currentRank = ranked.findIndex(e => e._isCurrentCandidate) + 1;

    if (!events.length) {
      root._dateConflictRanking = null;
      container.innerHTML = `
        <div class="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
          <div class="flex items-center gap-2 text-emerald-800">
            <i class="fa-solid fa-circle-check"></i>
            <strong class="text-sm">Sin conflictos para esta fecha</strong>
          </div>
        </div>
      `;
      container.classList.remove("hidden");
      return;
    }

    // "closed" = eventos que OCUPAN el día (confirmado/realizado o señal pendiente) → rojo.
    const closed = events.filter(e => STAGES_BLOCKING_DATE.includes(e.stage));
    const pipeline = events.filter(e => !STAGES_BLOCKING_DATE.includes(e.stage));
    const headerClass = closed.length
      ? "border-red-200 bg-red-50 text-red-900 hover:bg-red-100"
      : "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100";

    // Desglose por tipo para distinguir confirmados de señales pendientes.
    const confirmedCount = closed.filter(e => e.stage === "confirmed" || e.stage === "done").length;
    const pendingCount = closed.filter(e => e.stage === "accepted_pending_deposit").length;
    const breakdown = [
      confirmedCount ? `${confirmedCount} confirmado${confirmedCount > 1 ? "s" : ""}` : null,
      pendingCount ? `${pendingCount} señal pendiente` : null,
    ].filter(Boolean).join(" · ");

    root._dateConflictRanking = { date, events, ranked, winner, currentRank, closed, pipeline };

    container.innerHTML = `
      <button type="button" id="event-date-conflicts-open-ranking" class="w-full rounded-xl border ${headerClass} px-3 py-2 text-left transition-colors">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <i class="fa-solid ${closed.length ? "fa-triangle-exclamation" : "fa-circle-info"}"></i>
              <strong class="text-sm">${closed.length ? "Día ocupado" : "Conflicto"}: hay ${events.length} evento${events.length > 1 ? "s" : ""} el mismo día${breakdown ? ` (${breakdown})` : ""}</strong>
            </div>
            <div class="mt-0.5 text-xs opacity-80 truncate">Ver ranking - actual #${currentRank}/${ranked.length} - mejor: ${escapeHtml(winner?.client_name_legacy || "-")}</div>
          </div>
          <i class="fa-solid fa-ranking-star flex-shrink-0"></i>
        </div>
      </button>
    `;
    container.classList.remove("hidden");
    $(root, "#event-date-conflicts-open-ranking")?.addEventListener("click", () => openConflictRankingModal(root));
  } catch (err) {
    console.error("Error fetching date conflicts:", err);
    container.classList.add("hidden");
  }
}

// Estados que "ocupan el día" a efectos de conflictos (NO de ingresos):
// una señal pendiente reserva la fecha igual que un confirmado/realizado.
const STAGES_BLOCKING_DATE = ["confirmed", "done", "accepted_pending_deposit"];

/**
 * Avisa si al pasar a Confirmado faltan documentos o no hay senal registrada.
 * Es un aviso, no un requisito: hay eventos propios o sin contrato. Devuelve
 * true para continuar.
 *
 * Ante un fallo de lectura no se interpone: seria absurdo impedir confirmar un
 * evento porque no hemos podido consultar sus adjuntos.
 */
async function confirmMissingDocuments(eventId, expectedBase = 0, depositPct = undefined) {
  try {
    const [documents, payments] = await Promise.all([
      listEventDocuments(eventId),
      listPaymentsByEvent(eventId),
    ]);

    const { items } = getConfirmationStatus(documents);
    const pending = items.filter(i => !i.done).map(i => `- ${i.label}`);

    const advanceTotal = (payments || [])
      .filter(p => p.payment_type === "advance")
      .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

    // La senal pactada es el porcentaje de reserva con IVA que fija el
    // contrato (events.deposit_pct), no una cifra fija.
    const expected = expectedBase > 0
      ? splitHonorarios(expectedBase, depositPct).reservaTotal
      : 0;

    if (advanceTotal <= 0) {
      pending.push("- Señal: no hay ningún ingreso registrado"
        + (expected > 0 ? ` (esperada: ${fmtEur(expected)})` : ""));
    } else if (expected > 0 && advanceTotal + 0.01 < expected) {
      pending.push(`- Señal: registrados ${fmtEur(advanceTotal)} de los ${fmtEur(expected)} pactados`);
    }

    if (!pending.length) return true;

    return confirm(
      `Este evento no cumple todo lo habitual para confirmar:\n\n${pending.join("\n")}\n\n`
      + `Puedes continuar igualmente (eventos propios, sin contrato o con otro acuerdo de pago).\n\n`
      + `¿Confirmar el evento?`
    );
  } catch (err) {
    console.warn("No se pudieron comprobar los requisitos del evento:", err);
    return true;
  }
}

function fmtEur(n) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR", useGrouping: "always",
  }).format(Number(n) || 0);
}

async function confirmBlockingDateConflict(date, { excludeId = null, teamId = null } = {}) {
  if (!date) return true;

  try {
    const events = await listEventsByDate(date, { excludeId });
    const blocking = events.filter(e => STAGES_BLOCKING_DATE.includes(e.stage));
    if (!blocking.length) return true;

    const confirmedCount = blocking.filter(e => e.stage === "confirmed" || e.stage === "done").length;
    const pendingCount = blocking.filter(e => e.stage === "accepted_pending_deposit").length;

    const sameTeamCount = blocking.filter(e => teamId && e.team_id === teamId).length;
    const lines = blocking
      .slice(0, 5)
      .map(e => `- ${e.client_name_legacy || "Evento sin nombre"} (${STAGE_LABELS[e.stage] || e.stage})`)
      .join("\n");
    const extra = blocking.length > 5 ? `\n- +${blocking.length - 5} mas` : "";
    const sameTeamText = sameTeamCount ? `\n\n${sameTeamCount} de ellos son de la misma banda/equipo.` : "";

    const parts = [];
    if (confirmedCount) parts.push(`${confirmedCount} confirmado/realizado${confirmedCount > 1 ? "s" : ""}`);
    if (pendingCount) parts.push(`${pendingCount} con señal pendiente`);
    const summary = parts.join(" y ");

    return confirm(
      `El día ${date} ya está ocupado (${summary}):\n\n${lines}${extra}${sameTeamText}\n\n¿Quieres ocupar igualmente esta fecha con este evento?`
    );
  } catch (err) {
    console.warn("No se pudieron comprobar conflictos de fecha:", err);
    return confirm("No se pudo comprobar si hay otros eventos ocupando ese día. ¿Quieres continuar igualmente?");
  }
}

function fillForm(root, ev) {
  $(root, "#event-name") && ($(root, "#event-name").value = ev.client_name_legacy || "");
  $(root, "#event-date") && ($(root, "#event-date").value = ev.date || todayISO());
  $(root, "#event-stage") && ($(root, "#event-stage").value = ev.stage || "lead");
  $(root, "#event-channel") && ($(root, "#event-channel").value = ev.channel || "");
  $(root, "#event-type") && ($(root, "#event-type").value = ev.type || "");
  // region se llena con loadAndFillRegions, aquí solo seteamos valor si ya se cargó,
  // pero fillForm se llama ANTES de loadAndFillRegions en openEditEventModal?
  // No, openEdit llama a wireSelectors (que llama a loadRegions) antes de fillForm?
  // Revisar orden en openEditEventModal.
  // En openEditEventModal: wireSelectors -> fetchEvent -> fillForm.
  // Entonces wireSelectors ya llenó el select. fillForm cambiará el valor.
  // Y luego llamamos a updateForecast.
  let regionForDefaults = null;
  if ($(root, "#event-region")) {
    regionForDefaults = normalizeRegion(ev.region);
    $(root, "#event-region").value = regionForDefaults || "";
  }

  // El switch debe quedar fijado ANTES de calcular el hint de precio,
  // porque el hint depende de su estado (con/sin equipo).
  if (ev.equipment_included === true || ev.equipment_included === false) {
    setEquipmentToggle(root, ev.equipment_included);
  } else {
    // Evento sin valor guardado (legacy o nuevo sin region aun):
    // usar el default del tramo de la region actual.
    setEquipmentToggle(root, Boolean(getTierInfoForRegion(regionForDefaults)?.productionIncluded));
  }

  if (regionForDefaults) updateForecast(root, regionForDefaults);

  $(root, "#event-price") && ($(root, "#event-price").value = ev.amount ?? "");
  $(root, "#event-notes") && ($(root, "#event-notes").value = ev.notes || "");
  $(root, "#event-encargado") && ($(root, "#event-encargado").value = ev.owner_id || "");

  const stage = ev.stage || "lead";
  const reasonCont = $(root, "#event-lost-reason-container");
  const reasonInput = $(root, "#event-rejected-reason");

  if (reasonCont && reasonInput) {
    if (stage === "rechazado") {
      reasonCont.classList.remove("hidden");
      reasonInput.value = ev.rejected_reason || "";
    } else {
      reasonCont.classList.add("hidden");
      reasonInput.value = "";
    }
  }
}

function setModeHeader(root, { mode, eventId }) {
  const title = $(root, "#event-modal-title");
  const sub = $(root, "#event-modal-subtitle");
  const modeEl = $(root, "#event-modal-mode");
  const idEl = $(root, "#event-modal-id");
  const del = $(root, "#event-modal-delete");

  if (mode === "create") {
    title && (title.textContent = "Nuevo evento");
    sub && (sub.textContent = "Completa los datos del evento.");
    del?.classList.add("hidden");
    idEl && (idEl.textContent = "—");
  } else {
    title && (title.textContent = "Editar evento");
    sub && (sub.textContent = "Edita los datos del evento.");
    del?.classList.remove("hidden");
    idEl && (idEl.textContent = eventId || "—");
  }

  modeEl && (modeEl.textContent = mode);
}

function wireSaveDelete(root, { mode, eventId, teamId, closeFn, onSaved, role }) {
  const saveBtn = $(root, "#event-modal-save");
  const deleteBtn = $(root, "#event-modal-delete");

  // Viewer: no save/delete (read-only modal)
  if (role === 'viewer') {
    saveBtn?.classList.add("hidden");
    deleteBtn?.classList.add("hidden");
    return;
  }

  // Admin/manager: both save and delete visible.

  saveBtn?.addEventListener("click", async () => {
    try {
      const res = await saveEvent({ mode, eventId, teamId, root });
      if (!res.ok) return;

      actions.addToast?.({ type: "success", message: mode === "create" ? "Evento creado" : "Evento actualizado" });
      closeFn();
      onSaved?.(res.event);
    } catch (e) {
      actions.addToast?.({ type: "error", message: String(e?.message || e) });
    }
  });

  // Only wire delete for admin/manager
  if (role === 'admin' || role === 'manager') {
    deleteBtn?.addEventListener("click", async () => {
      if (mode !== "edit") return;
      if (!confirm("¿Estás seguro de que quieres eliminar este evento?")) return;
      try {
        const supabase = getSupabaseClient();
        const { error } = await supabase.from("events").delete().eq("id", eventId);
        if (error) throw error;

        actions.addToast?.({ type: "success", message: "Evento eliminado" });
        closeFn();
        onSaved?.({ id: eventId, deleted: true });
      } catch (e) {
        actions.addToast?.({ type: "error", message: String(e?.message || e) });
      }
    });
  }
}


// Activity Logic
async function renderActivities(root, eventId) {
  const listEl = $(root, "#event-activities-list");
  const nextEl = $(root, "#event-next-activity");
  if (!listEl) return;

  listEl.innerHTML = '<div class="text-center p-2"><i class="fa-solid fa-spinner fa-spin text-slate-400"></i></div>';

  try {
    // 1. Render List
    const activities = await listActivitiesByEvent(eventId);
    if (!activities || activities.length === 0) {
      listEl.innerHTML = '<div class="text-sm text-slate-400 italic p-6 text-center border border-dashed border-slate-200 rounded-2xl">No hay actividades registradas.</div>';
    } else {
      listEl.innerHTML = activities.map(a => {
        const date = new Date(a.scheduled_date).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
        const isCompleted = a.status === 'completed';
        const statusColor = isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
        const typeIcon = {
          call: 'fa-phone', email: 'fa-envelope', meeting: 'fa-users',
          whatsapp: 'fa-whatsapp', visit: 'fa-map-location-dot', follow_up: 'fa-clock',
          proposal: 'fa-file-invoice-dollar', contract: 'fa-file-contract', payment: 'fa-euro-sign'
        }[a.type] || 'fa-calendar-check';

        return `
              <div class="activity-item border border-slate-100 p-3 rounded-xl mb-2 text-sm hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer group bg-white" data-id="${a.id}">
                  <div class="flex justify-between items-start gap-3">
                     <div class="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                        <i class="fa-solid ${typeIcon} text-xs"></i>
                     </div>
                     <div class="flex-1 min-w-0">
                         <div class="font-bold text-slate-900 flex items-center gap-2">
                             <span class="truncate">${escapeHtml(a.subject)}</span>
                             <span class="text-[10px] font-normal text-slate-400 whitespace-nowrap">• ${date}</span>
                         </div>
                         <div class="text-slate-500 text-xs mt-0.5 line-clamp-1">${escapeHtml(a.description || '(Sin descripción)')}</div>
                         ${a.assigned_profile ? `<div class="text-[10px] text-blue-600 font-bold mt-1 uppercase tracking-wider flex items-center gap-1">
                            <i class="fa-solid fa-user-check text-[8px]"></i> ${a.assigned_profile.display_name}
                         </div>` : ''}
                     </div>
                     <div class="flex flex-col items-end gap-2">
                         <span class="text-[10px] px-2 py-0.5 rounded-full font-bold ${statusColor} uppercase tracking-tighter">
                             ${a.status}
                         </span>
                         ${!isCompleted ? `
                            <button class="complete-act-btn opacity-0 group-hover:opacity-100 p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all" data-id="${a.id}" title="Completar">
                              <i class="fa-solid fa-check-double"></i>
                            </button>
                         ` : ''}
                     </div>
                  </div>
              </div>
          `;
      }).join("");

      // Wire clicks for editing
      listEl.querySelectorAll(".activity-item").forEach(item => {
        item.addEventListener("click", (e) => {
          if (e.target.closest('.complete-act-btn')) return;
          openEditActivityModal(item.dataset.id, {
            onSaved: () => renderActivities(root, eventId)
          });
        });
      });

      // Wire clicks for completion
      listEl.querySelectorAll(".complete-act-btn").forEach(btn => {
        btn.onclick = async (e) => {
          e.stopPropagation();
          // Open edit modal - user can then use the set status or the "Complete" button there
          openEditActivityModal(btn.dataset.id, {
            onSaved: () => renderActivities(root, eventId)
          });
        };
      });
    }

    // 2. Render Next Activity Preview
    const next = await getNextActivity(eventId);
    if (next && nextEl) {
      nextEl.classList.remove("hidden");
      $(nextEl, "#event-next-activity-type").textContent = next.type.toUpperCase();
      $(nextEl, "#event-next-activity-subject").textContent = next.subject;
      $(nextEl, "#event-next-activity-date").textContent = new Date(next.scheduled_date).toLocaleDateString("es-ES", { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
      $(nextEl, "#event-next-activity-assigned").textContent = next.assigned_profile ? `Asignado a: ${next.assigned_profile.display_name}` : 'Sin asignar';

      const compBtn = $(nextEl, "#event-next-activity-complete");
      if (compBtn) {
        compBtn.onclick = () => {
          openEditActivityModal(next.id, {
            onSaved: () => renderActivities(root, eventId)
          });
        };
      }
    } else if (nextEl) {
      nextEl.classList.add("hidden");
    }

    // Re-apply edit mode to new dynamic buttons
    setEditMode(root, root.getAttribute("data-is-editable") === "true");

  } catch (e) {
    console.error("Error loading activities:", e);
    listEl.innerHTML = `<div class="text-red-400 text-xs p-2">Error cargando actividades.</div>`;
  }
}

async function renderSecondaryContacts(root, eventId) {
  const listEl = $(root, "#event-secondary-contacts-list");
  if (!listEl) return;

  listEl.innerHTML = '<div class="text-center p-2"><i class="fa-solid fa-spinner fa-spin text-slate-400"></i></div>';

  try {
    const allLinks = await listEventContacts(eventId);
    root._eventContactLinks = allLinks;
    // Filtrar los que NO son primarios
    const secondary = allLinks.filter(l => !l.is_primary);

    if (secondary.length === 0) {
      listEl.innerHTML = '<div class="text-xs text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded-xl">No hay contactos adicionales vinculados.</div>';
      return;
    }

    listEl.innerHTML = secondary.map(l => {
      const c = l.contacts;
      return `
        <div class="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl group">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-semibold text-slate-900 truncate">${escapeHtml(c?.name)}</div>
            <div class="text-xs text-slate-500 truncate">${escapeHtml(c?.email || c?.phone || "Sin contacto")}</div>
          </div>
          <button class="remove-contact-btn text-slate-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-link-id="${l.id}" title="Desvincular">
            <i class="fa-solid fa-trash-can"></i>
          </button>
        </div>
      `;
    }).join("");

    // Wire remove buttons
    listEl.querySelectorAll(".remove-contact-btn").forEach(btn => {
      btn.onclick = async () => {
        if (!confirm("¿Desvincular este contacto del evento?")) return;
        try {
          await removeEventContact(btn.dataset.linkId);
          await renderSecondaryContacts(root, eventId);
          renderSecondaryContactPicker(root, eventId);
        } catch (e) {
          actions.addToast?.({ type: "error", message: "Error al desvincular contacto" });
        }
      };
    });

    // Re-apply edit mode to new dynamic buttons
    setEditMode(root, root.getAttribute("data-is-editable") === "true");

  } catch (e) {
    console.error("Error rendering contacts:", e);
    listEl.innerHTML = `<div class="text-red-400 text-xs p-2">Error cargando contactos.</div>`;
  }
}

function contactDisplayName(contact) {
  return [contact?.name, contact?.surname].filter(Boolean).join(" ").trim()
    || contact?.company
    || "Contacto sin nombre";
}

function contactSearchHaystack(contact) {
  return normalizeSearchText([
    contact?.name,
    contact?.surname,
    contact?.company,
    contact?.email,
    contact?.phone,
  ].filter(Boolean).join(" "));
}

function renderSecondaryContactPicker(root, eventId, { query = null } = {}) {
  const picker = $(root, "#event-secondary-contact-picker");
  const input = $(root, "#event-secondary-contact-search");
  const resultsEl = $(root, "#event-secondary-contact-results");
  if (!picker || !input || !resultsEl) return;

  const q = query === null ? input.value : query;
  const normalizedQuery = normalizeSearchText(q);
  const linkedIds = new Set((root._eventContactLinks || []).map(link => link.contact_id));
  const contacts = (root._contactsData || [])
    .filter(contact => !linkedIds.has(contact.id))
    .filter(contact => !normalizedQuery || contactSearchHaystack(contact).includes(normalizedQuery))
    .slice(0, 12);

  const createButton = `
    <button type="button" data-secondary-create-contact
      class="w-full mt-1 px-3 py-2 rounded-lg border border-dashed border-blue-200 bg-white text-left text-sm font-bold text-blue-700 hover:bg-blue-50">
      <i class="fa-solid fa-plus mr-1"></i>Crear contacto nuevo${q ? ` "${escapeHtml(q)}"` : ""}
    </button>
  `;

  if (!contacts.length) {
    resultsEl.innerHTML = `
      <div class="px-3 py-3 rounded-lg bg-white border border-blue-100 text-xs text-slate-500">
        No hay contactos existentes disponibles con esa busqueda.
      </div>
      ${createButton}
    `;
  } else {
    resultsEl.innerHTML = `
      ${contacts.map(contact => {
        const name = contactDisplayName(contact);
        const meta = [contact.email, contact.phone, contact.company].filter(Boolean).join(" · ");
        return `
          <button type="button" data-secondary-contact-id="${escapeHtml(contact.id)}"
            class="w-full px-3 py-2 rounded-lg bg-white border border-blue-100 hover:border-blue-300 hover:bg-blue-50 text-left transition-colors">
            <span class="block text-sm font-bold text-slate-900 truncate">${escapeHtml(name)}</span>
            <span class="block text-xs text-slate-500 truncate">${escapeHtml(meta || "Sin email/telefono")}</span>
          </button>
        `;
      }).join("")}
      ${createButton}
    `;
  }
}

function wireSecondaryContactPicker(root, eventId, teamId) {
  const addBtn = $(root, "#event-contact-add-secondary");
  const picker = $(root, "#event-secondary-contact-picker");
  const input = $(root, "#event-secondary-contact-search");
  const resultsEl = $(root, "#event-secondary-contact-results");
  if (!addBtn || !picker || !input || !resultsEl) return;
  if (addBtn._secondaryPickerWired) return;
  addBtn._secondaryPickerWired = true;

  addBtn.addEventListener("click", () => {
    picker.classList.toggle("hidden");
    if (!picker.classList.contains("hidden")) {
      input.disabled = false;
      addBtn.disabled = false;
      input.focus();
      renderSecondaryContactPicker(root, eventId);
    }
  });

  input.addEventListener("input", () => renderSecondaryContactPicker(root, eventId));

  resultsEl.addEventListener("click", async (ev) => {
    const createBtn = ev.target.closest("[data-secondary-create-contact]");
    if (createBtn) {
      ev.preventDefault();
      openCreateContactModal({
        onSaved: async (created) => {
          if (!created?.id) return;
          try {
            await addEventContact(eventId, created.id);
            await loadAndFillContacts(root, teamId);
            await renderSecondaryContacts(root, eventId);
            input.value = "";
            renderSecondaryContactPicker(root, eventId);
            actions.addToast?.({ type: "success", message: "Contacto vinculado" });
          } catch (e) {
            actions.addToast?.({ type: "error", message: String(e?.message || e) });
          }
        }
      });
      return;
    }

    const contactBtn = ev.target.closest("[data-secondary-contact-id]");
    if (!contactBtn) return;

    ev.preventDefault();
    try {
      await addEventContact(eventId, contactBtn.getAttribute("data-secondary-contact-id"));
      await renderSecondaryContacts(root, eventId);
      input.value = "";
      renderSecondaryContactPicker(root, eventId);
      actions.addToast?.({ type: "success", message: "Contacto vinculado" });
    } catch (e) {
      actions.addToast?.({ type: "error", message: String(e?.message || e) });
    }
  });
}

// =========================
// Documents Dropdown
// =========================
function wireDocumentsDropdown(root, ev) {
  const wrapper = root.querySelector("#event-docs-dropdown-wrapper");
  const btn = root.querySelector("#event-docs-dropdown-btn");
  const menu = root.querySelector("#event-docs-dropdown-menu");

  if (!btn || !menu || !wrapper) return;

  const btnPresupuesto = root.querySelector("#event-doc-presupuesto");
  const btnContrato = root.querySelector("#event-doc-contrato");
  const btnConfirmacion = root.querySelector("#event-doc-confirmacion");

  // Logic: Unlock documents based on stage progression
  // Stages: lead -> contacted -> negotiation -> confirmed -> done (or rechazado)
  // Solo el contrato y el recibo de la senal estan operativos. La factura ya
  // descuenta la senal cobrada, pero sigue oculta hasta acordar con la
  // gestoria si la senal lleva su propia factura de anticipo (su IVA se
  // devenga al cobrarla). El resto de plantillas siguen con la senal fija de
  // 500 EUR. Se reactivara cada uno al revisar su plantilla; la condicion de
  // fase original se conserva para no tener que reconstruirla.
  const DOCS_PENDIENTES_DE_REVISION = true;
  const unlockIfReviewed = (cond) => !DOCS_PENDIENTES_DE_REVISION && cond;

  const isPresupuestoUnlocked = unlockIfReviewed(
    ["proposal_sent", "negotiation", "confirmed", "done"].includes(ev.stage)
  );
  // Contrato: se abre en "Aceptado - senal pendiente", que es cuando hay algo
  // que firmar ("El 30% a la firma del presente contrato"), y sigue disponible
  // despues para poder reemitirlo. Antes de esa fase no se ofrece.
  // Ademas, la plantilla esta redactada para bodas ("Los NOVIOS"): hasta que
  // existan las de empresa y particular, no se ofrece en esos eventos.
  const isContratoUnlocked =
    ["accepted_pending_deposit", "confirmed", "done"].includes(ev.stage) && ev.type === "boda";
  const isConfirmacionUnlocked = unlockIfReviewed(["confirmed", "done"].includes(ev.stage));
  // Recibo de la senal: desde que puede haber senal cobrada. Si de verdad la
  // hay se comprueba al pulsar, porque puede anotarse con el modal abierto.
  const isReciboUnlocked = DOCUMENTS_TAB_STAGES.includes(ev.stage);

  // Show/Hide individual items
  if (btnPresupuesto) {
    btnPresupuesto.style.display = isPresupuestoUnlocked ? "flex" : "none";
    if (isPresupuestoUnlocked) btnPresupuesto.classList.remove("hidden");
  }
  if (btnContrato) {
    btnContrato.style.display = isContratoUnlocked ? "flex" : "none";
    if (isContratoUnlocked) btnContrato.classList.remove("hidden");
  }
  if (btnConfirmacion) {
    btnConfirmacion.style.display = isConfirmacionUnlocked ? "flex" : "none";
    if (isConfirmacionUnlocked) btnConfirmacion.classList.remove("hidden");
  }

  // Show/Hide Main Dropdown Button
  const anyDocAvailable = isPresupuestoUnlocked || isContratoUnlocked || isConfirmacionUnlocked || isReciboUnlocked;
  if (ev && ev.id && anyDocAvailable) {
    btn.classList.remove("hidden");
  } else {
    btn.classList.add("hidden");
    return;
  }

  // Toggle menu
  btn.onclick = (e) => {
    e.stopPropagation();
    menu.classList.toggle("hidden");
  };

  // Close menu when clicking outside
  const closeMenu = (e) => {
    if (!wrapper.contains(e.target)) {
      menu.classList.add("hidden");
    }
  };
  document.removeEventListener("click", closeMenu); // Avoid duplicates if rerendered
  document.addEventListener("click", closeMenu);


  function generateWithPreflight(opts) {
    menu.classList.add("hidden");
    return generateDocumentWithPreflight(ev, opts);
  }

  // Wire buttons
  if (btnPresupuesto) btnPresupuesto.onclick = () => generateWithPreflight({ docType: "presupuesto", docTypeCode: "P",    docLabel: "Presupuesto",  generatorFn: generatePresupuestoPDF });
  if (btnContrato)    btnContrato.onclick    = () => generateWithPreflight({ docType: "contrato",    docTypeCode: "C",    docLabel: "Contrato",     generatorFn: generateContratoPDF });
  if (btnConfirmacion) btnConfirmacion.onclick = () => generateWithPreflight({ docType: "confirmacion",docTypeCode: "CONF",docLabel: "Confirmación", generatorFn: generateConfirmacionPDF });

  // Phase 2 Buttons
  const btnFactura = root.querySelector("#event-doc-factura");
  const btnRecibo  = root.querySelector("#event-doc-recibo");
  const btnProforma= root.querySelector("#event-doc-proforma");

  const isFacturaUnlocked  = unlockIfReviewed(["confirmed", "done"].includes(ev.stage));
  const isProformaUnlocked = unlockIfReviewed(["negotiation", "confirmed", "done"].includes(ev.stage));

  if (btnFactura) {
    btnFactura.style.display = isFacturaUnlocked ? "flex" : "none";
    if (isFacturaUnlocked) btnFactura.classList.remove("hidden");
    btnFactura.onclick = () => {
      menu.classList.add("hidden");
      generateFinalInvoice(ev);
    };
  }
  if (btnRecibo) {
    btnRecibo.style.display = isReciboUnlocked ? "flex" : "none";
    if (isReciboUnlocked) btnRecibo.classList.remove("hidden");
    btnRecibo.onclick = () => {
      menu.classList.add("hidden");
      generateDepositReceipt(ev);
    };
  }
  if (btnProforma) {
    btnProforma.style.display = isProformaUnlocked ? "flex" : "none";
    if (isProformaUnlocked) btnProforma.classList.remove("hidden");
    btnProforma.onclick = () => generateWithPreflight({ docType: "proforma", docTypeCode: "PRO", docLabel: "Proforma",     generatorFn: generateProformaPDF });
  }
}

// Abre el preflight y, al generar, anota el numero consumido en el registro
// documental del evento.
async function generateDocumentWithPreflight(ev, { docType, docTypeCode, docLabel, generatorFn }) {
  try {
    actions.addToast?.({ type: "info", message: `Preparando ${docLabel}...` });
    let contact = {};
    try {
      const result = await getPrimaryContactForEvent(ev.id);
      if (result?.contact) contact = result.contact;
    } catch { /* use empty contact */ }

    await openDocumentPreflightModal({
      event: ev,
      contact,
      docType,      // 'factura' | 'contrato' | 'proforma' | 'presupuesto' | 'confirmacion' | 'recibo_senal'
      docTypeCode,  // 'F' | 'C' | 'PRO' | 'P' | 'CONF' | 'R'
      docLabel,
      onGenerate: async (event, enrichedContact, companyData, docNumber, extraData) => {
        await generatorFn(event, enrichedContact, companyData, docNumber, extraData);

        // El numero correlativo ya se ha consumido: se anota para que no
        // quede ningun numero sin rastro y para poder adjuntarle despues el
        // documento firmado. No bloquea la descarga si falla.
        try {
          const teamId = await getCurrentTeamId({ profileOptions: { force: false, allowNull: true } });
          const user = await getUser();
          await registerIssuedDocument({
            teamId,
            eventId: event.id,
            kind: docType,
            docType: docTypeCode,
            docNumber,
            userId: user?.id || null,
          });
        } catch (e) {
          console.warn("[docs] no se pudo registrar el documento emitido:", e);
        }

        actions.addToast?.({ type: "success", message: `${docLabel} descargado` });
      },
    });
  } catch (e) {
    console.error(`Error generating ${docLabel}:`, e);
    actions.addToast?.({ type: "error", message: `Error generando ${docLabel}` });
  }
}

// Senal cobrada: suma de los pagos de tipo "advance" (los que crea el
// justificante o se anotan en Pagos).
async function getRegisteredDeposit(eventId) {
  const advances = (await listPaymentsByEvent(eventId))
    .filter((p) => p.payment_type === "advance");
  const amount = Math.round(advances.reduce((acc, p) => acc + (Number(p.amount) || 0), 0) * 100) / 100;
  return { amount, advances };
}

// Recibo de la senal: el importe es lo cobrado de verdad, no una cifra fija.
// Sin senal registrada no se emite.
async function generateDepositReceipt(ev) {
  const { amount, advances } = await getRegisteredDeposit(ev.id);

  if (amount <= 0) {
    actions.addToast?.({
      type: "warning",
      message: "No hay señal registrada: súbela con su justificante o anótala en Pagos antes de emitir el recibo.",
    });
    return;
  }

  // El metodo solo se indica si consta en todos los pagos: los anotados desde
  // Pagos no lo guardan.
  const method = advances.every((p) => p.method === "transfer") ? "Transferencia bancaria" : null;

  await generateDocumentWithPreflight(ev, {
    docType: DOC_KINDS.RECIBO_SENAL,
    docTypeCode: "R",
    docLabel: "Recibo de señal",
    generatorFn: (event, contact, companyData, docNumber) =>
      generateReciboPDF(event, contact, companyData, docNumber, { amount, method }),
  });
}

// Factura final: descuenta la senal cobrada de verdad, no una cifra fija.
async function generateFinalInvoice(ev) {
  const { amount: advanceTotal } = await getRegisteredDeposit(ev.id);

  await generateDocumentWithPreflight(ev, {
    docType: "factura",
    docTypeCode: "F",
    docLabel: "Factura",
    generatorFn: (event, contact, companyData, docNumber) =>
      generateFacturaPDF(event, contact, companyData, docNumber, { advanceTotal }),
  });
}


// API pública
export async function openCreateEventModal({ onSaved, initialData = {} } = {}) {
  await requireSession();
  const teamId = await getCurrentTeamId({ profileOptions: { force: true, allowNull: false } });

  if (!window.CRM?.modals?.openModal) {
    actions.addToast?.({ type: "error", message: "Modal system no disponible." });
    return;
  }

  const modalEl = window.CRM.modals.openModal("event-modal-template");
  const closeFn = () => window.CRM?.modals?.closeAllModals?.();

  patchModalSelectsToDB(modalEl);
  setModeHeader(modalEl, { mode: "create" });

  // defaults + initialData
  fillForm(modalEl, {
    date: todayISO(),
    stage: "lead",
    amount: null,
    client_name_legacy: initialData.client_name_legacy || "",
    ...initialData
  });

  const role = await getRole((await getUser()).id);

  wireBaseButtons(modalEl, closeFn);
  await wireSelectors(modalEl, teamId, getSelectorDeps());

  // Date conflict check for create mode
  const createDate = initialData.date || todayISO();
  renderDateConflicts(modalEl, createDate, null, teamId);

  // Team availability check for create mode: no event/lineup exists yet, but we can
  // already tell the user who on the whole roster is unavailable for the selected date.
  let createUnavailability = [];
  let createMembers = [];
  try {
    [createUnavailability, createMembers] = await Promise.all([
      listUnavailabilityForTeam(teamId),
      listLineupMembers(teamId),
    ]);
  } catch (e) {
    console.warn("[EventModal] Error cargando disponibilidad del equipo", e);
  }
  refreshLineupAvailabilityWarnings(modalEl, { date: createDate }, createUnavailability, createMembers);
  $(modalEl, "#event-date")?.addEventListener("change", () => {
    const newDate = $(modalEl, "#event-date")?.value;
    renderDateConflicts(modalEl, newDate, null, teamId);
    refreshLineupAvailabilityWarnings(modalEl, { date: newDate }, createUnavailability, createMembers);
  });

  // If we have an initial contactId, select it after wireSelectors has loaded them
  if (initialData.contactId) {
    await loadAndFillContacts(modalEl, teamId, { selectId: initialData.contactId });
  }

  setEditMode(modalEl, true);

  wireTabs(modalEl, role);
  wireSaveDelete(modalEl, { mode: "create", eventId: null, teamId, closeFn, onSaved, role });

  if (role === 'viewer') {
    restrictViewerUI(modalEl);
  }

  showErrors(modalEl, []);
}


export async function openEditEventModal(eventId, { onSaved, initialTab } = {}) {
  if (!eventId) return;

  await requireSession();
  const teamId = await getCurrentTeamId({ profileOptions: { force: true, allowNull: false } });

  if (!window.CRM?.modals?.openModal) {
    actions.addToast?.({ type: "error", message: "Modal system no disponible." });
    return;
  }

  const modalEl = window.CRM.modals.openModal("event-modal-template");
  const closeFn = () => window.CRM?.modals?.closeAllModals?.();

  patchModalSelectsToDB(modalEl);
  setModeHeader(modalEl, { mode: "edit", eventId });

  const role = await getUserRole();
  if (role === 'viewer') {
    modalEl.dataset.viewerExpenseOnly = "true";
  }

  wireBaseButtons(modalEl, closeFn);
  await wireSelectors(modalEl, teamId, getSelectorDeps());

  $(modalEl, "#event-modal-edit-toggle")?.addEventListener("click", () => {
    setEditMode(modalEl, true);
  });

  setEditMode(modalEl, false);

  try {
    const ev = await fetchEventById(eventId);
    fillForm(modalEl, ev);

    // Date conflict alerts
    renderDateConflicts(modalEl, ev.date, eventId, teamId);

    // Secondary Contacts
    await renderSecondaryContacts(modalEl, eventId);
    wireSecondaryContactPicker(modalEl, eventId, teamId);

    // Wiring Tabs & Finance & Emails & Lineup
    wireTabs(modalEl, role, initialTab, { event: ev });
    wireFinanceLogic(modalEl, ev.id, teamId, ev, { setEditMode });
    wireEmailsLogic(modalEl, ev.id);
    wireLineupLogic(modalEl, ev.id, teamId, ev, { onSaved });
    wireSetlistCard(modalEl, ev, { role }).catch((e) => console.warn("[setlist] tarjeta:", e));
    wireSuppliersCard(modalEl, ev, { role, teamId }).catch((e) => console.warn("[proveedores] tarjeta:", e));
    if (DOCUMENTS_TAB_STAGES.includes(ev.stage)) {
      wireDocumentsTab(modalEl, ev, {
        onStageChange: () => onSaved?.(),
        generateReceipt: () => generateDepositReceipt(ev),
      });
    }


    // [Documents Dropdown Logic]
    wireDocumentsDropdown(modalEl, ev);

    const cId = await fetchPrimaryContactId(eventId);
    const vId = await fetchPrimaryVenueId(eventId);

    if (cId) {
      await loadAndFillContacts(modalEl, teamId, { selectId: cId });
    } else {
      const cs = $(modalEl, "#event-contact-select");
      if (cs) cs.value = "";
      setContactPreview(modalEl, null);
    }

    if (vId) {
      await loadAndFillVenues(modalEl, teamId, { selectId: vId });
    } else {
      const vs = $(modalEl, "#event-venue-select");
      if (vs) vs.value = "";
      setVenuePreview(modalEl, null);
      updateVenueDistanceAndPrice(modalEl, teamId, null);
    }

    // Activities Wiring
    await renderActivities(modalEl, eventId);
    $(modalEl, "#event-activity-create")?.addEventListener("click", () => {
      openCreateActivityModal({
        eventId,
        assigned_to: ev.owner_id, // Default to event owner
        onSaved: () => renderActivities(modalEl, eventId)
      });
    });

  } catch (e) {
    actions.addToast?.({ type: "error", message: String(e?.message || e) });
  }

  if (role === 'viewer') {
    restrictViewerUI(modalEl);
  }

  wireSaveDelete(modalEl, { mode: "edit", eventId, teamId, closeFn, onSaved, role });
  showErrors(modalEl, []);
}


// =========================
// LINEUP LOGIC
// =========================

const PRODUCTION_AREAS = Object.freeze([
  { id: "documentation", taskCategories: ["documentation"] },
  { id: "equipment", taskCategories: ["lineup"] },
  { id: "technical", taskCategories: ["technical"] },
  { id: "transport", taskCategories: ["transport"] },
]);

const CORE_DOCUMENTATION_TASKS = Object.freeze([
  "Cliente fiscal",
  "NIF/CIF",
  "Direccion fiscal",
  "Email facturacion",
  "Precio final",
  "Senal recibida",
  "Forma de pago",
  "Condiciones contrato",
]);
// Tarima, luces y sonido ya no son tareas: salen de event_suppliers (tarea 4b).
const CORE_TECHNICAL_TASKS = Object.freeze(["Furgoneta"]);
const CORE_SCHEDULE_TASKS = Object.freeze([
  "Hora de carga en el local",
  "Llegada a la finca",
  "Horario de montaje inicio",
  "Horario de montaje fin maximo",
  "Horario bolo",
]);
const CORE_PRODUCTION_TASKS = Object.freeze([...CORE_DOCUMENTATION_TASKS, ...CORE_TECHNICAL_TASKS, ...CORE_SCHEDULE_TASKS]);

function getLineupStats(root) {
  const total = ROLE_ORDER.length;
  const assigned = ROLE_ORDER.reduce((acc, role) => {
    const sel = $(root, `#event-lineup-${role}`);
    return acc + (sel?.value ? 1 : 0);
  }, 0);
  return { assigned, total };
}

function dateDistanceLabel(dateStr) {
  const iso = normStr(dateStr).slice(0, 10);
  if (!iso) return "-";
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "Pasado";
  if (diffDays === 0) return "Hoy";
  return `${diffDays}d`;
}

function normalizeTaskText(v) {
  return normStr(v).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function isCoreProductionTask(task) {
  const subject = normalizeTaskText(task?.subject);
  return CORE_PRODUCTION_TASKS.some(label => normalizeTaskText(label) === subject);
}

function findCoreTask(tasks, subject) {
  const normalized = normalizeTaskText(subject);
  return (tasks || []).find(t => normalizeTaskText(t?.subject) === normalized) || null;
}

const SUPPLIER_TASK_VALUE_PREFIX = "supplier:";

const TECHNICAL_SUPPLIER_TYPES_BY_SUBJECT = Object.freeze({
  [normalizeTaskText("Furgoneta")]: [SUPPLIER_TYPES.TRANSPORT],
});

const TECHNICAL_SPECIAL_OPTIONS_BY_SUBJECT = Object.freeze({
  [normalizeTaskText("Furgoneta")]: ["No hace falta"],
});

function supplierTaskValue(contactId) {
  return `${SUPPLIER_TASK_VALUE_PREFIX}${contactId}`;
}

function supplierIdFromTaskValue(value) {
  const v = normStr(value);
  return v.startsWith(SUPPLIER_TASK_VALUE_PREFIX) ? v.slice(SUPPLIER_TASK_VALUE_PREFIX.length) : "";
}

function supplierTypes(contact) {
  const values = Array.isArray(contact?.supplier_types) && contact.supplier_types.length
    ? contact.supplier_types
    : [contact?.supplier_type].filter(Boolean);
  return [...new Set(values.filter(Boolean))];
}

function supplierDisplayName(contact) {
  const person = [contact?.name, contact?.surname].filter(Boolean).join(" ").trim();
  return normStr(contact?.company) || person || "Proveedor sin nombre";
}

function supplierOptionLabel(contact) {
  const typeLabel = supplierTypes(contact).map(type => SUPPLIER_TYPE_LABELS[type] || type).join(" + ");
  return [supplierDisplayName(contact), typeLabel].filter(Boolean).join(" - ");
}

function sortSuppliersForSubject(suppliers, subject) {
  const preferredTypes = TECHNICAL_SUPPLIER_TYPES_BY_SUBJECT[normalizeTaskText(subject)] || [];
  return [...(suppliers || [])].sort((a, b) => {
    const aPreferred = supplierTypes(a).some(type => preferredTypes.includes(type)) ? 0 : 1;
    const bPreferred = supplierTypes(b).some(type => preferredTypes.includes(type)) ? 0 : 1;
    if (aPreferred !== bPreferred) return aPreferred - bPreferred;
    return supplierDisplayName(a).localeCompare(supplierDisplayName(b), "es", { sensitivity: "base" });
  });
}

async function loadSupplierContactsForTeam(teamId) {
  if (!teamId) return [];
  const contacts = await listContactsByTeam(teamId, {
    orderBy: "name",
    ascending: true,
    select: "id, name, surname, company, is_supplier, supplier_type, supplier_types",
  });
  return (contacts || []).filter(contact => Boolean(contact?.is_supplier));
}

function isMusicianVisibleLogisticsTask(task) {
  const subject = normalizeTaskText(task?.subject);
  return subject.includes("horario de montaje inicio") || subject.includes("horario bolo");
}

function formatTaskDateTime(dateStr) {
  const s = normStr(dateStr);
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function logisticsTaskValue(task) {
  const subject = normStr(task?.subject);
  const description = normStr(task?.description);
  if (description && normalizeTaskText(description) !== normalizeTaskText(subject)) return description;

  const notes = normStr(task?.outcome_notes);
  if (notes) return notes;

  const scheduled = formatTaskDateTime(task?.scheduled_date);
  if (scheduled) return scheduled;

  return "Horario pendiente";
}

function applyViewerProductionLayout(root) {
  const title = $(root, "#event-tab-content-lineup h3");
  if (title) title.textContent = "Informacion del evento";

  const sub = $(root, "#event-production-summary-sub");
  if (sub) sub.textContent = "Datos operativos necesarios para el musico.";

  const progressBox = $(root, "#event-logistics-progress-text")?.closest(".flex.items-center.gap-3");
  progressBox?.classList.add("hidden");

  const kpiGrid = $(root, "#event-production-kpi-lineup")?.closest(".grid");
  kpiGrid?.classList.add("hidden");

  const chipBar = root.querySelector(".event-production-chip")?.parentElement;
  chipBar?.classList.add("hidden");

  root.querySelectorAll("[data-production-area]").forEach(area => {
    const isTransport = area.getAttribute("data-production-area") === "transport";
    area.classList.toggle("hidden", !isTransport);
    if (isTransport) {
      area.querySelector(".event-production-area-body")?.classList.remove("hidden");
      area.querySelector("[data-production-chevron]")?.classList.add("hidden");
      const titleEl = area.querySelector(".block.text-sm");
      if (titleEl) titleEl.textContent = "Horario de montaje";
      const subEl = $(root, "#event-production-area-transport-sub");
      if (subEl) subEl.textContent = "Hora definida por el equipo de produccion.";
    }
  });
}

function setProductionText(root, id, value) {
  const el = $(root, `#${id}`);
  if (el) el.textContent = value;
}

function areaTasks(tasks, area) {
  const cats = new Set(area.taskCategories);
  return (tasks || []).filter(t => cats.has(t.logistics_category));
}

function taskStats(tasks) {
  const active = (tasks || []).filter(t => t.status !== "cancelled");
  const completed = active.filter(t => t.status === "completed").length;
  return {
    total: active.length,
    completed,
    pending: Math.max(0, active.length - completed),
  };
}

function setProductionAreaOpen(root, areaId) {
  root._activeProductionArea = areaId;
  root.querySelectorAll("[data-production-area]").forEach(areaEl => {
    const isOpen = areaEl.dataset.productionArea === areaId;
    areaEl.querySelector(".event-production-area-body")?.classList.toggle("hidden", !isOpen);
    areaEl.querySelector("[data-production-chevron]")?.classList.toggle("rotate-180", isOpen);
  });

  root.querySelectorAll("[data-production-open]").forEach(btn => {
    const isActive = btn.dataset.productionOpen === areaId;
    btn.classList.toggle("bg-slate-900", isActive);
    btn.classList.toggle("text-white", isActive);
    btn.classList.toggle("bg-white", !isActive);
    btn.classList.toggle("text-slate-600", !isActive);
  });
}

function wireProductionAccordions(root) {
  root.querySelectorAll(".event-production-area-toggle").forEach(btn => {
    btn.addEventListener("click", () => {
      const area = btn.closest("[data-production-area]")?.dataset.productionArea;
      if (area) setProductionAreaOpen(root, area);
    });
  });
  root.querySelectorAll("[data-production-open]").forEach(btn => {
    btn.addEventListener("click", () => setProductionAreaOpen(root, btn.dataset.productionOpen));
  });
  setProductionAreaOpen(root, "equipment");
}

function updateProductionSummary(root, eventData, tasks = root._productionTasks || []) {
  root._productionTasks = tasks || [];
  const productionTasks = root._productionTasks.filter(isCoreProductionTask);
  const lineup = getLineupStats(root);
  const logisticsTasks = taskStats(productionTasks);
  // Tarima, luces y sonido cuentan solo cuando se han cargado (evento
  // confirmado y ficha de admin o manager).
  const supplierStats = Array.isArray(root._eventSupplierLines)
    ? technicalSupplierStats(root._eventSupplierLines)
    : { total: 0, completed: 0, pending: 0 };
  const productionTotals = {
    total: logisticsTasks.total + lineup.total + supplierStats.total,
    completed: logisticsTasks.completed + lineup.assigned + supplierStats.completed,
  };
  productionTotals.pending = Math.max(0, productionTotals.total - productionTotals.completed);
  const lineupComplete = lineup.assigned === lineup.total;
  const percent = productionTotals.total ? Math.round((productionTotals.completed / productionTotals.total) * 100) : 0;
  const dateLabel = dateDistanceLabel($(root, "#event-date")?.value || eventData?.date);

  setProductionText(root, "event-production-kpi-lineup", `${lineup.assigned}/${lineup.total}`);
  setProductionText(root, "event-production-kpi-tasks", `${productionTotals.completed}/${productionTotals.total}`);
  setProductionText(root, "event-production-kpi-pending", String(productionTotals.pending));
  setProductionText(root, "event-production-kpi-date", dateLabel);
  const docStats = taskStats(areaTasks(productionTasks, PRODUCTION_AREAS.find(area => area.id === "documentation")));
  setProductionText(root, "event-production-chip-documentation", `${docStats.completed}/${docStats.total}`);
  setProductionText(root, "event-production-chip-equipment", `${lineup.assigned}/${lineup.total}`);
  setProductionText(root, "event-logistics-progress-text", `${percent}%`);

  const sub = $(root, "#event-production-summary-sub");
  if (sub) {
    const lineupText = lineupComplete ? "lineup completo" : `faltan ${lineup.total - lineup.assigned} roles`;
    const taskText = productionTotals.pending ? `${productionTotals.pending} puntos pendientes` : "logistica lista";
    sub.textContent = `${lineupText} - ${taskText} - ${dateLabel}`;
  }

  const progress = $(root, "#event-logistics-progress-bar");
  if (progress) {
    progress.style.width = `${percent}%`;
    const colorClass = percent === 100 ? "bg-emerald-500" : percent >= 70 ? "bg-amber-500" : "bg-blue-500";
    progress.className = `h-2 rounded-full transition-all ${colorClass}`;
  }

  for (const area of PRODUCTION_AREAS) {
    const stats = area.id === "equipment"
      ? { total: lineup.total, completed: lineup.assigned, pending: Math.max(0, lineup.total - lineup.assigned) }
      : taskStats(areaTasks(productionTasks, area));
    if (area.id === "technical") {
      stats.total += supplierStats.total;
      stats.completed += supplierStats.completed;
      stats.pending += supplierStats.pending;
    }
    if (area.id !== "equipment") {
      setProductionText(root, `event-production-chip-${area.id}`, `${stats.completed}/${stats.total}`);
    }
    const subEl = $(root, `#event-production-area-${area.id}-sub`);
    if (subEl) {
      if (area.id === "equipment") {
        const text = `${lineup.assigned}/${lineup.total} asignados`;
        subEl.textContent = lineupComplete ? `${text} - OK` : `${text} - faltan ${stats.pending}`;
      } else {
        subEl.textContent = stats.total ? `${stats.completed}/${stats.total} puntos - ${stats.pending} pendientes` : "Sin puntos en esta area.";
      }
    }
  }
}

function renderTaskRows(tasks, emptyText = "No hay tareas pendientes en esta area.", { readOnly = false, musicianView = false } = {}) {
  const active = (tasks || []).filter(t => t.status !== "cancelled");
  const pending = active.filter(t => t.status !== "completed");
  const completed = active.filter(t => t.status === "completed");

  const completionDetails = (t) => {
    const details = [];
    if (t.completed_date) {
      details.push(`<span><span class="font-bold text-slate-500">Completada:</span> ${escapeHtml(formatTaskDateTime(t.completed_date))}</span>`);
    }
    if (t.outcome && !(t.is_logistics_task && t.outcome === "other")) {
      details.push(`<span><span class="font-bold text-slate-500">Resultado:</span> ${escapeHtml(getActivityOutcomeLabel(t.outcome))}</span>`);
    }
    if (t.outcome_notes) {
      details.push(`<span class="block sm:col-span-2"><span class="font-bold text-slate-500">Notas:</span> ${escapeHtml(t.outcome_notes)}</span>`);
    }
    if (!details.length) {
      details.push(`<span class="text-amber-700"><span class="font-bold">Sin detalle de cierre.</span> Edita la tarea para indicar resultado y notas.</span>`);
    }
    return `<div class="mt-2 grid gap-1 text-xs text-slate-600 bg-white border border-slate-100 rounded-lg px-2.5 py-2 sm:grid-cols-2">${details.join("")}</div>`;
  };

  const row = (t, isCompleted = false) => `
    <div class="flex items-start gap-3 p-3 rounded-xl ${isCompleted ? "bg-emerald-50/50 text-slate-700 border-emerald-100" : "bg-white hover:bg-slate-50 text-slate-700 border-slate-100"} border group/task" data-task-id="${escapeHtml(t.id)}">
      ${readOnly ? `<span class="mt-0.5 flex-shrink-0"><i class="${isCompleted ? "fa-solid fa-circle-check text-emerald-500" : "fa-regular fa-circle text-slate-300"}"></i></span>` : `<button type="button" class="mt-0.5 flex-shrink-0 focus:outline-none task-toggle-btn" data-id="${escapeHtml(t.id)}" data-status="${escapeHtml(t.status)}" title="Cambiar estado">
        <i class="${isCompleted ? "fa-solid fa-circle-check text-emerald-500" : "fa-regular fa-circle text-slate-300 hover:text-slate-400"}"></i>
      </button>`}
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium flex items-center justify-between gap-2">
          <span>${escapeHtml(t.subject)}</span>
          ${readOnly ? "" : `<button type="button" class="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-500 hover:border-blue-200 hover:text-blue-600 edit-task-btn" data-id="${escapeHtml(t.id)}" title="Editar tarea CRM"><i class="fa-solid fa-pen"></i><span>Editar</span></button>`}
        </div>
        ${musicianView ? `<div class="text-sm font-black text-slate-900 mt-1">${escapeHtml(logisticsTaskValue(t))}</div>` : (t.description ? `<div class="text-xs text-slate-500 mt-1 whitespace-pre-line">${escapeHtml(t.description)}</div>` : "")}
        ${!musicianView && isCompleted ? completionDetails(t) : ""}
      </div>
    </div>
  `;

  if (musicianView) {
    return active.length
      ? `<div class="space-y-1">${active.map(t => row(t, normStr(t.status) === "completed")).join("")}</div>`
      : `<div class="text-xs text-slate-400 border border-dashed border-slate-200 rounded-xl p-3 text-center">${emptyText}</div>`;
  }

  const pendingHtml = pending.length
    ? `<div class="space-y-1">${pending.map(t => row(t, false)).join("")}</div>`
    : (completed.length ? "" : `<div class="text-xs text-slate-400 border border-dashed border-slate-200 rounded-xl p-3 text-center">${emptyText}</div>`);

  const completedHtml = completed.length
    ? `<div class="pt-2">
        <div class="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-emerald-700">
          <i class="fa-solid fa-circle-check"></i>
          <span>Tareas completadas (${completed.length})</span>
        </div>
        <div class="space-y-1">${completed.map(t => row(t, true)).join("")}</div>
      </div>`
    : "";

  return `<div class="space-y-2">${pendingHtml}${completedHtml}</div>`;
}

function renderCoreDocumentationTasks(tasks, { readOnly = false } = {}) {
  const fields = [
    ["Cliente fiscal", "Cliente fiscal", "Razon social o nombre completo"],
    ["NIF/CIF", "NIF/CIF", "Ej: 12345678A"],
    ["Direccion fiscal", "Direccion fiscal", "Calle, CP, ciudad"],
    ["Email facturacion", "Email facturacion", "facturacion@cliente.com"],
    ["Precio final", "Precio final", "Ej: 3500 EUR"],
    ["Senal recibida", "Senal recibida", "Ej: 500 EUR"],
    ["Forma de pago", "Forma de pago", "Transferencia, efectivo..."],
    ["Condiciones contrato", "Condiciones contrato", "Condiciones especiales pactadas"],
  ];

  return `
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
      ${fields.map(([subject, label, placeholder]) => {
        const task = findCoreTask(tasks, subject);
        const value = normStr(task?.description) || normStr(task?.outcome_notes);
        const notes = normStr(task?.description) ? normStr(task?.outcome_notes) : "";
        const complete = task?.status === "completed" && value;
        return `
          <div class="rounded-xl border ${complete ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"} p-3">
            <label class="block">
              <span class="mb-1 flex items-center justify-between gap-2">
                <span class="text-[10px] font-black uppercase tracking-wider text-slate-500">${escapeHtml(label)}</span>
                <span class="text-[10px] font-bold ${complete ? "text-emerald-700" : "text-slate-400"}">${complete ? "OK" : "Pendiente"}</span>
              </span>
              <input type="text" class="production-task-input w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-400 focus:outline-none" data-task-id="${escapeHtml(task?.id || "")}" value="${escapeHtml(value)}" placeholder="${escapeHtml(placeholder)}" ${readOnly ? "disabled" : ""}>
            </label>
            <label class="block mt-3">
              <span class="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Notas</span>
              <textarea class="production-task-note w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs focus:border-slate-400 focus:outline-none" rows="2" data-task-id="${escapeHtml(task?.id || "")}" placeholder="Notas rapidas..." ${readOnly ? "disabled" : ""}>${escapeHtml(notes)}</textarea>
            </label>
          </div>
        `;
      }).join("")}
    </div>
  `;
}

function renderCoreTechnicalTasks(tasks, { readOnly = false, suppliers = [], supplierLines = [] } = {}) {
  const fields = [
    ["Furgoneta", "Furgoneta"],
  ];

  return `
    <div data-technical-suppliers>${renderTechnicalSuppliersSummary(supplierLines, suppliers)}</div>
    <div class="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      ${fields.map(([subject, label]) => {
        const task = findCoreTask(tasks, subject);
        const value = normStr(task?.description) || normStr(task?.outcome_notes);
        const notes = normStr(task?.description) ? normStr(task?.outcome_notes) : "";
        const complete = task?.status === "completed" && value;
        const selectedSupplierId = supplierIdFromTaskValue(value);
        const supplierRows = sortSuppliersForSubject(suppliers, subject);
        const specialOptions = TECHNICAL_SPECIAL_OPTIONS_BY_SUBJECT[normalizeTaskText(subject)] || [];
        const legacyValue = value && !selectedSupplierId && !specialOptions.includes(value) ? value : "";
        const selectDisabled = readOnly || (!supplierRows.length && !specialOptions.length);
        return `
          <div class="rounded-xl border ${complete ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"} p-3">
            <label class="block">
              <span class="mb-1 flex items-center justify-between gap-2">
              <span class="text-[10px] font-black uppercase tracking-wider text-slate-500">${escapeHtml(label)}</span>
              <span class="text-[10px] font-bold ${complete ? "text-emerald-700" : "text-slate-400"}">${complete ? "OK" : "Pendiente"}</span>
              </span>
              <select class="production-task-input w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-400 focus:outline-none" data-task-id="${escapeHtml(task?.id || "")}" ${selectDisabled ? "disabled" : ""}>
                <option value="">Sin proveedor vinculado</option>
                ${legacyValue ? `<option value="${escapeHtml(legacyValue)}" selected>Valor actual: ${escapeHtml(legacyValue)}</option>` : ""}
                ${supplierRows.map(contact => {
                  const optionValue = supplierTaskValue(contact.id);
                  return `<option value="${escapeHtml(optionValue)}" ${optionValue === value ? "selected" : ""}>${escapeHtml(supplierOptionLabel(contact))}</option>`;
                }).join("")}
                ${specialOptions.map(option => `<option value="${escapeHtml(option)}" ${option === value ? "selected" : ""}>${escapeHtml(option)}</option>`).join("")}
              </select>
            </label>
            ${!supplierRows.length ? '<p class="mt-2 text-[11px] text-amber-700">No hay proveedores creados en Contactos.</p>' : ""}
            <label class="block mt-3">
              <span class="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Notas</span>
              <textarea class="production-task-note w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs focus:border-slate-400 focus:outline-none" rows="2" data-task-id="${escapeHtml(task?.id || "")}" placeholder="Notas rápidas..." ${readOnly ? "disabled" : ""}>${escapeHtml(notes)}</textarea>
            </label>
          </div>
        `;
      }).join("")}
    </div>
  `;
}

function renderCoreScheduleTasks(tasks, { readOnly = false } = {}) {
  const fields = [
    { subject: "Hora de carga en el local", label: "Carga local", placeholder: "Ej: 15:30" },
    { subject: "Llegada a la finca", label: "Llegada finca", placeholder: "Ej: 17:00" },
    { subject: "Horario de montaje inicio", label: "Montaje inicio", placeholder: "Ej: 17:15" },
    { subject: "Horario de montaje fin maximo", label: "Montaje fin max.", placeholder: "Ej: 19:00" },
    { subject: "Horario bolo", label: "Horario bolo", placeholder: "Ej: 22:00 - 00:00" },
  ];

  return `
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
      ${fields.map(({ subject, label, placeholder, options = null }) => {
        const task = findCoreTask(tasks, subject);
        const value = normStr(task?.description) || normStr(task?.outcome_notes);
        const notes = normStr(task?.description) ? normStr(task?.outcome_notes) : "";
        const complete = task?.status === "completed";
        const controlHtml = options
          ? `<select class="production-task-input w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-400 focus:outline-none" data-task-id="${escapeHtml(task?.id || "")}" ${readOnly ? "disabled" : ""}>
              <option value="">Sin definir</option>
              ${options.map(option => `<option value="${escapeHtml(option)}" ${value === option ? "selected" : ""}>${escapeHtml(option)}</option>`).join("")}
            </select>`
          : `<input type="text" class="production-task-input w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-400 focus:outline-none" data-task-id="${escapeHtml(task?.id || "")}" value="${escapeHtml(value)}" placeholder="${escapeHtml(placeholder)}" ${readOnly ? "disabled" : ""}>`;
        return `
          <div class="rounded-xl border ${complete ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"} p-3">
            <label class="block">
              <span class="mb-1 flex items-center justify-between gap-2">
                <span class="text-[10px] font-black uppercase tracking-wider text-slate-500">${escapeHtml(label)}</span>
                <span class="text-[10px] font-bold ${complete ? "text-emerald-700" : "text-slate-400"}">${complete ? "OK" : "Pendiente"}</span>
              </span>
              ${controlHtml}
            </label>
            <label class="block mt-3">
              <span class="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Notas</span>
              <textarea class="production-task-note w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs focus:border-slate-400 focus:outline-none" rows="2" data-task-id="${escapeHtml(task?.id || "")}" placeholder="Notas rápidas..." ${readOnly ? "disabled" : ""}>${escapeHtml(notes)}</textarea>
            </label>
          </div>
        `;
      }).join("")}
    </div>
  `;
}

function markProductionDirty(root) {
  const btn = $(root, "#event-lineup-save");
  if (!btn) return;
  btn.dataset.dirty = "true";
  btn.classList.remove("bg-slate-900");
  btn.classList.add("bg-blue-600", "hover:bg-blue-700");
}

async function saveCoreProductionTasks(root) {
  const updates = [];

  root.querySelectorAll(".production-task-check[data-task-id]").forEach(input => {
    if (!input.dataset.taskId) return;
    const note = normStr(root.querySelector(`.production-task-note[data-task-id="${CSS.escape(input.dataset.taskId)}"]`)?.value);
    updates.push(updateLogisticsTask(input.dataset.taskId, {
      status: input.checked ? "completed" : "pending",
      outcome: input.checked ? "other" : null,
      outcome_notes: note || null,
      completed_date: input.checked ? new Date().toISOString() : null,
    }));
  });

  root.querySelectorAll(".production-task-input[data-task-id]").forEach(input => {
    if (!input.dataset.taskId) return;
    const value = normStr(input.value);
    const note = normStr(root.querySelector(`.production-task-note[data-task-id="${CSS.escape(input.dataset.taskId)}"]`)?.value);
    updates.push(updateLogisticsTask(input.dataset.taskId, {
      description: value || null,
      status: value ? "completed" : "pending",
      outcome: value ? "other" : null,
      outcome_notes: note || null,
      completed_date: value ? new Date().toISOString() : null,
    }));
  });

  await Promise.all(updates);
}

function refreshLineupAvailabilityWarnings(root, eventData, unavailability, members) {
  const date = $(root, "#event-date")?.value || eventData?.date || "";

  const blocksForDate = date
    ? (unavailability || []).filter(u => u.date_from <= date && date <= u.date_to)
    : [];
  const blocksByMember = new Map(blocksForDate.map(u => [u.member_id, u]));

  // Per-role select warnings: only meaningful if the person assigned to that role is unavailable.
  root.querySelectorAll("[data-lineup-role]").forEach(sel => {
    const role = sel.dataset.lineupRole;
    const warn = root.querySelector(`[data-lineup-warn="${role}"]`);
    if (!warn) return;

    const block = sel.value ? blocksByMember.get(sel.value) : null;
    if (block) {
      const textEl = warn.querySelector("[data-lineup-warn-text]");
      if (textEl) textEl.textContent = `No disponible${block.reason ? ": " + block.reason : ""}`;
      warn.classList.remove("hidden");
    } else {
      warn.classList.add("hidden");
    }
  });

  // Full-roster sampling: everyone in the team who's unavailable this date,
  // regardless of whether they're currently assigned to a lineup role.
  const unavailableMembers = (members || []).filter(m => blocksByMember.has(m.id));
  const namesWithReason = unavailableMembers.map(m => {
    const block = blocksByMember.get(m.id);
    return block?.reason ? `${m.display_name} (${block.reason})` : m.display_name;
  });
  const summaryText = namesWithReason.length
    ? `No disponible${namesWithReason.length > 1 ? "s" : ""} esta fecha: ${namesWithReason.join(", ")}`
    : "";

  // Lineup tab banner (near the role selects)
  const banner = $(root, "#event-lineup-availability-warning");
  const bannerText = $(root, "#event-lineup-availability-warning-text");
  if (banner) {
    if (namesWithReason.length) {
      if (bannerText) bannerText.textContent = summaryText;
      banner.classList.remove("hidden");
    } else {
      banner.classList.add("hidden");
    }
  }

  // General tab banner (right under the event date field, visible without opening the lineup tab)
  const dateBanner = $(root, "#event-lineup-date-warning");
  const dateBannerText = $(root, "#event-lineup-date-warning-text");
  if (dateBanner) {
    if (namesWithReason.length) {
      if (dateBannerText) dateBannerText.textContent = summaryText;
      dateBanner.classList.remove("hidden");
    } else {
      dateBanner.classList.add("hidden");
    }
  }
}

async function wireLineupLogic(root, eventId, teamId, eventData, { onSaved } = {}) {
  if (!eventId || !teamId) return;
  root._onProductionSaved = onSaved;

  try {
    const readOnlyProduction = root.dataset.viewerExpenseOnly === "true";
    if (readOnlyProduction) {
      $(root, "#event-lineup-defaults")?.classList.add("hidden");
      $(root, "#event-lineup-save")?.classList.add("hidden");
      applyViewerProductionLayout(root);
    }

    // 1. Load all team_members for this team
    const members = await listLineupMembers(teamId);

    // 1b. Load team unavailability once, to flag lineup conflicts on won/confirmed dates
    let unavailability = [];
    try {
      unavailability = await listUnavailabilityForTeam(teamId);
    } catch (e) {
      console.warn("[EventModal] Error cargando disponibilidad del equipo", e);
    }

    // 2. Populate each role select with ALL members
    const roleSelects = root.querySelectorAll('[data-lineup-role]');
    roleSelects.forEach(sel => {
      const current = sel.value;
      sel.innerHTML = '<option value="">— Sin asignar —</option>';
      for (const m of members) {
        const opt = document.createElement("option");
        opt.value = m.id;
        opt.textContent = m.display_name || "—";
        sel.appendChild(opt);
      }
      if (current) sel.value = current;
      sel.addEventListener("change", () => {
        updateProductionSummary(root, eventData);
        refreshLineupAvailabilityWarnings(root, eventData, unavailability, members);
      });
    });
    wireProductionAccordions(root);
    if (readOnlyProduction) applyViewerProductionLayout(root);
    $(root, "#event-date")?.addEventListener("change", () => {
      updateProductionSummary(root, eventData);
      refreshLineupAvailabilityWarnings(root, eventData, unavailability, members);
    });
    $(root, "#event-stage")?.addEventListener("change", () => refreshLineupAvailabilityWarnings(root, eventData, unavailability, members));

    // 3. Load existing lineup & fill selects
    const lineup = await getEventLineup(eventId);
    for (const entry of lineup) {
      const sel = $(root, `#event-lineup-${entry.role}`);
      if (sel) sel.value = entry.member_id || "";
    }
    updateProductionSummary(root, eventData);
    refreshLineupAvailabilityWarnings(root, eventData, unavailability, members);
    if (readOnlyProduction) applyViewerProductionLayout(root);

    // 4. Wire "Lineup habitual" button
    if (!readOnlyProduction) $(root, "#event-lineup-defaults")?.addEventListener("click", async () => {
      try {
        const defaults = await getDefaultLineup(teamId);
        for (const role of ROLE_ORDER) {
          const sel = $(root, `#event-lineup-${role}`);
          if (sel && defaults[role]) {
            sel.value = defaults[role];
          }
        }
        updateProductionSummary(root, eventData);
        refreshLineupAvailabilityWarnings(root, eventData, unavailability, members);
        actions.addToast?.({ type: "info", message: "Lineup habitual aplicado." });
      } catch (e) {
        actions.addToast?.({ type: "error", message: "Error cargando lineup habitual" });
      }
    });

    // 5. Wire "Enviar invite a la banda" (solo admin/manager; el propio helper aplica el gate)
    if (!readOnlyProduction) {
      wireInviteLogic(root, eventId, eventData).catch((e) => console.warn("[invite] wire error", e));
    }

    // ═══════════ LOGISTICS SECTION ═══════════
    const soundSelect = $(root, "#event-logistics-sound");
    const soundPrice = $(root, "#event-logistics-sound-price");
    const soundHint = $(root, "#event-logistics-sound-hint");
    const pacoSelect = $(root, "#event-logistics-paco");
    const pacoPrice = $(root, "#event-logistics-paco-price");
    const pacoRow = $(root, "#event-logistics-paco-row");
    const regionWarning = $(root, "#event-logistics-region-warning");
    const regionWarningText = $(root, "#event-logistics-region-warning-text");

    // Helper: get current region from the form
    function getCurrentRegion() {
      const regionSel = $(root, "#event-region");
      return (regionSel?.value || eventData?.region || "").toLowerCase().trim();
    }

    function isProductionIncluded(region) {
      const tierInfo = getTierInfoForRegion(region);
      return Boolean(tierInfo?.productionIncluded || SOUND_FREE_REGIONS.includes(region));
    }

    function updatePriceInputsState() {
      if (readOnlyProduction) {
        [soundPrice, pacoPrice].forEach(input => {
          if (!input) return;
          input.disabled = true;
          input.classList.add("opacity-50", "bg-slate-50", "cursor-not-allowed");
        });
        return;
      }

      if (soundSelect && soundPrice) {
        const isNo = soundSelect.value === 'no';
        soundPrice.disabled = isNo;
        if (isNo) {
          soundPrice.classList.add("opacity-50", "bg-slate-50");
          soundPrice.value = ''; // Clear price if 'no'
        } else {
          soundPrice.classList.remove("opacity-50", "bg-slate-50");
          // Restore default if empty and region known?
          // Better let updateLogisticsForRegion handle values
        }
      }
      if (pacoSelect && pacoPrice) {
        const isNo = pacoSelect.value === 'no';
        pacoPrice.disabled = isNo;
        if (isNo) {
          pacoPrice.classList.add("opacity-50", "bg-slate-50");
          pacoPrice.value = '';
        } else {
          pacoPrice.classList.remove("opacity-50", "bg-slate-50");
        }
      }
    }

    soundSelect?.addEventListener("change", () => {
      updatePriceInputsState();
      // If changed to yes/partial, maybe set default price if empty?
      if (soundSelect.value !== 'no' && !soundPrice.value) {
        const region = getCurrentRegion();
        soundPrice.value = isProductionIncluded(region) ? "0" : DEFAULT_SOUND_PRICE;
      }
    });

    pacoSelect?.addEventListener("change", () => {
      updatePriceInputsState();
      if (pacoSelect.value !== 'no' && !pacoPrice.value) {
        pacoPrice.value = isProductionIncluded(getCurrentRegion()) ? "0" : DEFAULT_PACO_PRICE;
      }
    });

    // Helper: update logistics UI based on region
    function updateLogisticsForRegion(region) {
      if (!region) {
        regionWarning?.classList.remove("hidden");
        if (regionWarningText) regionWarningText.textContent = "Selecciona una región en Datos Generales para ver la disponibilidad.";
        return;
      }
      regionWarning?.classList.add("hidden");

      const tierInfo = getTierInfoForRegion(region);
      const productionIncluded = isProductionIncluded(region);
      const isPacoAvailable = productionIncluded || PACO_REGIONS.includes(region);

      // Sound hint
      if (soundHint) {
        soundHint.textContent = productionIncluded
          ? `${tierInfo?.productionLabel || "Incluido en el precio sugerido"}. Precio: 0 EUR.`
          : `Nuestro equipo de PA + tecnico. Precio sugerido: ${DEFAULT_SOUND_PRICE} EUR.`;
      }

      // Auto-set price if default and not edited by user (and option is active)
      if (soundPrice && !soundPrice.dataset.userEdited && soundSelect?.value !== 'no') {
        soundPrice.value = productionIncluded ? "0" : DEFAULT_SOUND_PRICE;
      }

      if (pacoPrice && !pacoPrice.dataset.userEdited && pacoSelect?.value !== 'no') {
        pacoPrice.value = productionIncluded ? "0" : DEFAULT_PACO_PRICE;
      }

      // PACO visibility
      if (pacoRow) {
        if (isPacoAvailable) {
          pacoRow.classList.remove("hidden");
        } else {
          pacoRow.classList.add("hidden");
          if (pacoSelect) pacoSelect.value = 'no';
          updatePriceInputsState();
        }
      }
    }

    // Mark user edits so we don't overwrite
    soundPrice?.addEventListener("input", () => { soundPrice.dataset.userEdited = "true"; });
    pacoPrice?.addEventListener("input", () => { pacoPrice.dataset.userEdited = "true"; });

    // Listen for region changes in the form
    const regionSelect = $(root, "#event-region");
    regionSelect?.addEventListener("change", () => {
      soundPrice && delete soundPrice.dataset.userEdited;
      updateLogisticsForRegion(getCurrentRegion());

    // Render logistics checklist
    renderLogisticsChecklist(root, eventId, eventData, teamId);
    });

    // Load existing logistics data
    try {
      const logistics = await getEventLogistics(eventId);

      if (soundSelect) {
        // Handle legacy boolean true -> 'yes' if not migrated, or just use value
        // The migration ensures it is 'yes' or 'no', but let's be safe
        const val = logistics.has_own_sound;
        soundSelect.value = (val === true || val === 'true') ? 'yes' : (val || 'no');
      }

      if (soundPrice && logistics.sound_price != null) {
        soundPrice.value = logistics.sound_price;
        soundPrice.dataset.userEdited = "true";
      }

      if (pacoSelect) {
        const val = logistics.has_paco_stage;
        pacoSelect.value = (val === true || val === 'true') ? 'yes' : (val || 'no');
      }

      if (pacoPrice && logistics.paco_stage_price != null) {
        pacoPrice.value = logistics.paco_stage_price;
        pacoPrice.dataset.userEdited = "true";
      }

      updatePriceInputsState();

    } catch (e) {
      console.warn("Could not load logistics:", e);
    }

    // Initial region-based UI update
    updateLogisticsForRegion(getCurrentRegion());

    // Render logistics checklist
    renderLogisticsChecklist(root, eventId, eventData, teamId);

    // ═══════════ SAVE BUTTON (Lineup + Logistics) ═══════════
    if (!readOnlyProduction) $(root, "#event-lineup-save")?.addEventListener("click", async () => {
      try {
        // Save lineup
        const assignments = ROLE_ORDER.map(role => {
          const sel = $(root, `#event-lineup-${role}`);
          return { role, member_id: sel?.value || null };
        }).filter(a => a.member_id);

        await saveEventLineup(teamId, eventId, assignments);

        // Save logistics
        await saveEventLogistics(eventId, {
          has_own_sound: soundSelect?.value || 'no',
          sound_price: soundPrice?.value || 0,
          has_paco_stage: pacoSelect?.value || 'no',
          paco_stage_price: pacoPrice?.value || 0,
        });

        await saveCoreProductionTasks(root);
        await renderLogisticsChecklist(root, eventId, eventData, teamId);
        if (root._activeProductionArea) setProductionAreaOpen(root, root._activeProductionArea);
        await onSaved?.({ id: eventId, productionUpdated: true });

        const saveBtn = $(root, "#event-lineup-save");
        saveBtn?.classList.remove("bg-blue-600", "hover:bg-blue-700");
        saveBtn?.classList.add("bg-slate-900");
        if (saveBtn) delete saveBtn.dataset.dirty;

        actions.addToast?.({ type: "success", message: "Logistica guardada correctamente" });
      } catch (e) {
        console.error("Error saving logistics:", e);
        actions.addToast?.({
          type: "error", message: e?.message?.includes("ux_event_member")
            ? "Un miembro no puede tener dos roles en el mismo evento"
            : "Error al guardar la logística"
        });
      }
    });

  } catch (e) {
    console.error("Error wiring logistics:", e);
  }
}

// =========================
// FINANCE MODULE LOGIC
// =========================

// Fases a partir de las cuales hay documentacion que gestionar. Coincide con
// el desbloqueo del contrato: una pestana de documentos en la que no se puede
// generar ni recibir nada solo estorba.
const DOCUMENTS_TAB_STAGES = ["accepted_pending_deposit", "confirmed", "done"];

function wireTabs(root, role = 'manager', initialTab = "general", { event = null } = {}) {
  const docsTabVisible = !!event?.id && DOCUMENTS_TAB_STAGES.includes(event.stage);

  const tabs = [
    { id: "general", btn: $(root, "#event-tab-btn-general"), content: $(root, "#event-tab-content-general") },
    { id: "contacts", btn: $(root, "#event-tab-btn-contacts"), content: $(root, "#event-tab-content-contacts") },
    { id: "venue", btn: $(root, "#event-tab-btn-venue"), content: $(root, "#event-tab-content-venue") },
    { id: "activities", btn: $(root, "#event-tab-btn-activities"), content: $(root, "#event-tab-content-activities") },
    { id: "finance", btn: $(root, "#event-tab-btn-finance"), content: $(root, "#event-tab-content-finance"), roles: ['admin', 'manager', 'viewer'] },
    { id: "lineup", btn: $(root, "#event-tab-btn-lineup"), content: $(root, "#event-tab-content-lineup") },
    { id: "documents", btn: $(root, "#event-tab-btn-documents"), content: $(root, "#event-tab-content-documents"), roles: ['admin', 'manager'], visible: docsTabVisible },
    { id: "emails", btn: $(root, "#event-tab-btn-emails"), content: $(root, "#event-tab-content-emails"), roles: ['admin', 'manager'] },
  ];

  // Ocultar botones de pestañas que el rol no puede ver o que no aplican
  // todavía a este evento
  const isHidden = (t) => (t.roles && !t.roles.includes(role)) || t.visible === false;

  tabs.forEach(t => {
    if (isHidden(t)) {
      t.btn?.classList.add("hidden");
      t.content?.classList.add("hidden");
    }
  });



  function setTab(activeId) {
    tabs.forEach(t => {
      if (!t.btn || !t.content) return;
      if (t.id === activeId) {
        t.btn.classList.add("border-slate-900", "text-slate-900");
        t.btn.classList.remove("border-transparent", "text-slate-500");
        t.content.classList.remove("hidden");
      } else {
        t.btn.classList.remove("border-slate-900", "text-slate-900");
        t.btn.classList.add("border-transparent", "text-slate-500");
        t.content.classList.add("hidden");
      }
    });
  }

  tabs.forEach(t => {
    if (t.btn) {
      t.btn.onclick = () => setTab(t.id);
    }
  });

  // Default to general or initialTab. Si la pestaña pedida está oculta para
  // este rol o esta fase, setTab la haría visible: se cae a "general".
  const requested = initialTab || "general";
  const requestedTab = tabs.find(t => t.id === requested);
  setTab(!requestedTab || isHidden(requestedTab) ? "general" : requested);
}

/**
 * restrictViewerUI — read-only mode for viewers.
 * Viewers can only VIEW event info and ADD expenses.
 * All other editing, creation, and deletion is disabled.
 */
function restrictViewerUI(root) {
  root.dataset.viewerExpenseOnly = "true";

  // Disable all inputs, selects and textareas
  root.querySelectorAll("input, select, textarea").forEach(el => {
    el.disabled = true;
    el.classList.add("bg-slate-50", "cursor-not-allowed");
  });

  // Hide action buttons (create/edit/delete within tabs)
  const forbiddenSelectors = [
    "#event-contact-create",
    "#event-contact-edit",
    "#event-contact-clear",
    "#event-contact-add-secondary",
    "#event-venue-create",
    "#event-venue-edit",
    "#event-venue-clear",
    "#event-activity-create",
    "#event-next-activity-complete",
    "#event-payment-add",
    "#event-email-compose",
    "#event-modal-edit-toggle",
    "#event-lineup-save",
    "#event-lineup-defaults",
    ".task-toggle-btn",
    ".edit-task-btn",
    ".remove-contact-btn",
    ".delete-payment-btn",
    ".delete-expense-btn"
  ];

  forbiddenSelectors.forEach(sel => {
    root.querySelectorAll(sel).forEach(btn => btn.classList.add("hidden"));
  });

  // BUT allow expense addition for viewers
  const expAddBtn = $(root, "#finance-exp-add-btn");
  if (expAddBtn) {
    expAddBtn.classList.remove("hidden");
  }

  applyViewerExpenseMode(root);
}

// =========================
// VENUE COMBOBOX COMPONENT
// =========================
import { normalizeSearch } from "../../utils/stringUtils.js";

function wireVenueCombobox(root, teamId) {
  const searchInput = $(root, "#event-venue-search");
  const hiddenInput = $(root, "#event-venue-select");
  const dropdown = $(root, "#event-venue-dropdown");
  const listEl = $(root, "#event-venue-list");
  const createBtn = $(root, "#event-venue-create-dropdown");
  const createLabel = $(root, "#event-venue-create-label");
  const combobox = $(root, "#event-venue-combobox");

  if (!searchInput || !dropdown) return;

  function closeDropdown() {
    dropdown.classList.add("hidden");
    dropdown.classList.remove("flex");
  }

  function renderList(query = "") {
    const venues = root._venuesData || [];
    const normQ = normalizeSearch(query);

    // Sort logic could go here, but venues are initially sorted alphabetically.
    const filtered = normQ
      ? venues.filter(v => normalizeSearch(v.name).includes(normQ) || (v.city && normalizeSearch(v.city).includes(normQ)) || (v.address && normalizeSearch(v.address).includes(normQ)))
      : venues;

    listEl.innerHTML = "";

    if (filtered.length === 0) {
      listEl.innerHTML = `<div class="px-3 py-2 text-sm text-slate-500 italic">No se encontraron coincidencias...</div>`;
    } else {
      filtered.forEach(v => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "w-full text-left px-3 py-2 text-sm hover:bg-slate-100 transition-colors flex flex-col focus:bg-slate-100 focus:outline-none";

        const venueName = escapeHtml(v.name || "Venue sin nombre");
        // venues usa region o city
        const loc = [v.city, v.region].filter(Boolean).join(" · ").trim();
        const venueLoc = loc ? `<span class="text-xs text-slate-500 mt-0.5">${escapeHtml(loc)}</span>` : "";

        btn.innerHTML = `<span class="font-medium text-slate-900">${venueName}</span>${venueLoc}`;

        btn.onclick = async () => {
          hiddenInput.value = v.id;
          searchInput.value = v.name || "";
          closeDropdown();
          try {
            const fetched = await fetchVenue(v.id);
            setVenuePreview(root, fetched);
            updateVenueDistanceAndPrice(root, teamId, fetched);
          } catch (e) { }
        };
        listEl.appendChild(btn);
      });
    }

    if (normQ) {
      createLabel.textContent = `Crear "${query}"`;
    } else {
      createLabel.textContent = `Crear nuevo venue`;
    }
  }

  // To prevent multiple listeners on re-wire
  if (searchInput._comboboxWired) return;
  searchInput._comboboxWired = true;

  searchInput.addEventListener("focus", () => {
    // Si está en modo sólo lectura, no abrir
    if (root.getAttribute("data-is-editable") === "false") return;
    renderList(searchInput.value);
    dropdown.classList.remove("hidden");
    dropdown.classList.add("flex");
  });

  searchInput.addEventListener("input", () => {
    if (root.getAttribute("data-is-editable") === "false") return;

    // Si borra el texto, vaciar la selección
    if (!searchInput.value.trim()) {
      hiddenInput.value = "";
      setVenuePreview(root, null);
      updateVenueDistanceAndPrice(root, teamId, null);
    } else {
      // If typing, unset exact ID until selected
      if (hiddenInput.value) {
        // Optionally you can keep ID if the text matches exactly, but safer to clear
        hiddenInput.value = "";
        setVenuePreview(root, null);
        updateVenueDistanceAndPrice(root, teamId, null);
      }
    }
    renderList(searchInput.value);
    dropdown.classList.remove("hidden");
    dropdown.classList.add("flex");
  });

  // Close when clicking outside
  document.addEventListener("click", (e) => {
    if (combobox && !combobox.contains(e.target)) {
      closeDropdown();
      // Si salió sin seleccionar ID, si había un ID previo pero el texto no matchea,
      // dejamos que el form validación salte si es obligatorio o la DB se queda con vacío.
    }
  });

  createBtn.addEventListener("click", () => {
    closeDropdown();
    const q = searchInput.value.trim();
    setTimeout(() => {
      openCreateVenueModal({
        teamId,
        initialName: q,
        onSaved: async (created) => {
          if (!created?.id) return;
          try {
            await loadAndFillVenues(root, teamId, { selectId: created.id });
            wireVenueCombobox(root, teamId);
            actions.addToast?.({ type: "success", message: "Venue seleccionado" });
          } catch (e) {
            actions.addToast?.({ type: "error", message: String(e?.message || e) });
          }
        },
      });
    }, 0);
  });
}

export default {
  openCreateEventModal,
  openEditEventModal,
};




// =========================
// LOGISTICS CHECKLIST LOGIC
// =========================
async function renderLogisticsChecklist(root, eventId, eventData, teamId) {
  const readOnly = root.dataset.viewerExpenseOnly === "true";
  const isConfirmed = eventData.stage === "confirmed" || eventData.stage === "done";
  const taskContainers = PRODUCTION_AREAS
    .map(area => $(root, `#event-production-tasks-${area.id === "equipment" ? "lineup" : area.id}`))
    .filter(Boolean);

  if (!isConfirmed) {
    const inactive = `
      <div class="text-xs text-slate-400 border border-dashed border-slate-200 rounded-xl p-3 text-center">
        El checklist se activara cuando el evento pase a Confirmado.
      </div>`;
    taskContainers.forEach(el => { el.innerHTML = inactive; });
    root._eventSupplierLines = null;
    updateProductionSummary(root, eventData, []);
    return;
  }

  taskContainers.forEach(el => {
    el.innerHTML = '<div class="text-center p-4"><i class="fa-solid fa-spinner fa-spin text-slate-400"></i></div>';
  });

  try {
    if (!readOnly) {
      await createDefaultLogisticsTasks(eventId, teamId);
      await ensureCoreLogisticsTasks(eventId, teamId);
    }
    const tasks = await getLogisticsTasks(eventId);
    const coreTasks = (tasks || []).filter(isCoreProductionTask);
    const supplierContacts = await loadSupplierContactsForTeam(teamId).catch((error) => {
      console.warn("[EventModal] Could not load supplier contacts:", error);
      return [];
    });
    root._eventSupplierLines = readOnly ? null : await listEventSuppliers(eventId).catch((error) => {
      console.warn("[EventModal] Could not load event suppliers:", error);
      return null; // no cuenta en el progreso y el resumen avisa del error
    });
    root._productionTasks = coreTasks;
    root._supplierContacts = supplierContacts;
    root._supplierContactsById = new Map(supplierContacts.map(contact => [contact.id, contact]));
    updateProductionSummary(root, eventData, root._productionTasks);

    if (readOnly) {
      applyViewerProductionLayout(root);
      taskContainers.forEach(el => {
        el.innerHTML = "";
      });

      const transportTarget = $(root, "#event-production-tasks-transport");
      const musicianTasks = (tasks || []).filter(isMusicianVisibleLogisticsTask);
      if (transportTarget) {
        transportTarget.innerHTML = renderTaskRows(
          musicianTasks,
          "Horario de montaje pendiente.",
          { readOnly: true, musicianView: true }
        );
      }
      return;
    }

    if (!coreTasks.length) {
      taskContainers.forEach(el => {
        el.innerHTML = '<div class="text-xs text-slate-400 border border-dashed border-slate-200 rounded-xl p-3 text-center">No hay tareas logisticas.</div>';
      });
      return;
    }

    const documentationTarget = $(root, "#event-production-tasks-documentation");
    if (documentationTarget) documentationTarget.innerHTML = renderCoreDocumentationTasks(coreTasks, { readOnly });

    const lineupTarget = $(root, "#event-production-tasks-lineup");
    if (lineupTarget) {
      const lineup = getLineupStats(root);
      lineupTarget.innerHTML = `
        <div class="rounded-xl border ${lineup.assigned === lineup.total ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"} p-3 text-sm">
          <div class="flex items-center gap-2 font-black ${lineup.assigned === lineup.total ? "text-emerald-800" : "text-amber-800"}">
            <i class="fa-solid ${lineup.assigned === lineup.total ? "fa-circle-check" : "fa-triangle-exclamation"}"></i>
            <span>${lineup.assigned === lineup.total ? "Lineup completo" : `Faltan ${lineup.total - lineup.assigned} roles por asignar`}</span>
          </div>
          <p class="mt-1 text-xs text-slate-600">Este punto se calcula automaticamente con los campos de lineup de arriba.</p>
        </div>`;
    }

    const technicalTarget = $(root, "#event-production-tasks-technical");
    if (technicalTarget) technicalTarget.innerHTML = renderCoreTechnicalTasks(coreTasks, { readOnly, suppliers: supplierContacts, supplierLines: root._eventSupplierLines });
    // La tarjeta Proveedores avisa al guardar: se repinta solo el resumen.
    if (!root._onSuppliersChanged) {
      root._onSuppliersChanged = (e) => {
        if (!["confirmed", "done"].includes(eventData.stage)) return;
        root._eventSupplierLines = e.detail?.lines || [];
        const box = $(root, "[data-technical-suppliers]");
        if (box) box.innerHTML = renderTechnicalSuppliersSummary(root._eventSupplierLines, root._supplierContacts);
        updateProductionSummary(root, eventData);
      };
      root.addEventListener("event-suppliers:changed", root._onSuppliersChanged);
    }

    const transportTarget = $(root, "#event-production-tasks-transport");
    if (transportTarget) transportTarget.innerHTML = renderCoreScheduleTasks(coreTasks, { readOnly });

    root.querySelectorAll(".production-task-check, .production-task-input, .production-task-note").forEach(input => {
      input.addEventListener("change", () => {
        markProductionDirty(root);
      });
      input.addEventListener("input", () => {
        markProductionDirty(root);
      });
    });

    root.querySelectorAll("#event-production-areas .task-toggle-btn").forEach(btn => {
      btn.onclick = async () => {
        const id = btn.dataset.id;
        const current = btn.dataset.status;
        const next = current === "completed" ? "pending" : "completed";
        const area = btn.closest("[data-production-area]")?.dataset.productionArea || root._activeProductionArea;
        await updateLogisticsTask(id, { status: next });
        await renderLogisticsChecklist(root, eventId, eventData, teamId);
        if (area) setProductionAreaOpen(root, area);
        await root._onProductionSaved?.({ id: eventId, productionUpdated: true });
      };
    });

    root.querySelectorAll("#event-production-areas .edit-task-btn").forEach(btn => {
      btn.onclick = () => {
        openEditActivityModal(btn.dataset.id, {
          onSaved: () => {
            const area = btn.closest("[data-production-area]")?.dataset.productionArea || root._activeProductionArea;
            renderLogisticsChecklist(root, eventId, eventData, teamId).then(() => {
              if (area) setProductionAreaOpen(root, area);
              return root._onProductionSaved?.({ id: eventId, productionUpdated: true });
            });
          }
        });
      };
    });
  } catch (e) {
    console.error("Error loading logistics checklist:", e);
    taskContainers.forEach(el => {
      el.innerHTML = '<div class="p-4 text-red-500 text-sm">Error cargando tareas.</div>';
    });
  }
}
